import { env } from '@/libs/env'
import { errorHandler } from '@/libs/errors'
import { isEqual } from 'lodash'
import axios, { AxiosError, AxiosResponse } from 'axios'
import dayjs, { Dayjs } from 'dayjs'
import moment from 'moment'
import qs from 'qs'
import 'dayjs/locale/ja'
import axiosRetry from 'axios-retry'

// models
import { SalesDetailPdfParams, SalesSplitDetailPdfParams, PaymentMethodInput } from '@/models/payment-method'
import { IdentificationType } from '@/models/self-checkin/identification'
import { ReservationUserType } from '@/models/reservation/reservation-user'
import { ReservationGuestType } from '@/models/reservation/reservation-guest'
import { AdminReservationResponseType } from '@/models/reservation/admin-reservation'
import { ReservationData } from '@/models/reservation/reservation-data'
import { GuestRoomAssignResponseType, GuestRoomAutoAssignConfig } from '@/models/guest-room/guest-room-assign'
import { onlyForDev } from '@/utils/check-environment'
import { TMiwaCardKeyReservation, TMiwaCardKeyReservationParams } from '@/models/miwa-lock'

import { searchMockResult } from '@/apis/mock'
import * as auth0 from '@/apis/auth0'
import { AdvanceMoneySearchType } from '@/models/sales-manager/advance-money'
import { SalesDetailSearchType } from '@/models/sales-manager/sales'
import { SurveyFetchType, QuestionResponseType, convertQuestionToSurvey } from '@/models/survey'
import { CleaningStatusType, RoomUsageStatusType } from '@/constants/cleaning-manager'
import { TerminalType } from '@/models/smaregi/terminal'
import { deviceSettingResponseType } from '@/models/device/device-setting'
import { CsvUploadHistoryResponseType } from '@/models/csv-upload-history/csv-upload-history'
import { TransactionType } from '@/models/smaregi/transaction'
import { StaffInputType } from '@/models/staff'
import * as MobileRequest from '@/models/mobile-request'
import * as CLeaningManager from '@/constants/cleaning-manager'
import { GuestRoomTypeSelectType, GuestRoomTypeInputType } from '@/models/guest-room-type'

import { DeviceSendHistory } from '@/models/device/device-send-history'
import {
  OperationLogFiltersType,
  OperationLogLegacyFiltersType,
  OperationLogBatchDetailType,
  OperationLogDetailType,
  OperationLogListResponse,
  OperationLogLegacyListResponse,
  EmployeeType,
} from '@/models/operation-log'
import * as SiteControllerStockType from '@/models/room-manager/response/site-controller-stock'
import { PreparationBulkCreateRequestType, SiteControllerStockBulkUpdateRequestType } from '@/models/room-manager/bulk-operation'
import { EntireSearchContentType } from '@/models/entire-search'
import * as GuestRoomIndicator from '@/models/room-manager/response/guest-room-indicator'
import { ApprovedStatus, ApprovedStatusType } from '@/models/reservation-approved-status'
import { AssetType, FormatType, MapCategoryType, SpaceCreateAndUpdateRequestType } from '@/models/guest-app/asset'
import { DesignType, ThemeType } from '@/models/guest-app/design'
import { AssetFreeDetailFormValue } from '@/components/organisms/settings/guest-app/free-detail-modal'
import { AssetPulldownDetailFormValue } from '@/components/organisms/settings/guest-app/pulldown-asset-detail'
import { CustomCheckinListResponseType, CustomCheckinResponseType } from '@/models/self-checkin/custom-checkin'
import { AssetMapDetailFormValue } from '@/components/organisms/settings/guest-app/map-detail-modal'
import { AssetNewsDetailFormValue } from '@/components/organisms/settings/guest-app/news-detail-modal'
import { AssetButtonFormValue } from '@/components/organisms/settings/guest-app/button-asset-detail'
import { AssetImageDetailFormValue } from '@/components/organisms/settings/guest-app/image-detail-modal'
import { AssetCouponDetailFormValue } from '@/components/organisms/settings/guest-app/coupon-detail-modal'
import { AssetProductDetailFormValue } from '@/components/organisms/settings/guest-app/request-product-detail-modal'
import {
  SearchCheckOutReservation,
  SearchReservationList,
  SelfCheckInReservationSearchQuery,
  SearchReservationTypeWhenCheckedIn,
  SearchReservationTypeWhenNonCheckIn,
  SearchReservationEachRoom,
} from '@/models/reservation'
import { CalculatedSalesSubjectSetType, CalculatedSalesSubjectTaxType, DiscountSettingType } from '@/models/sales'
import {
  DeliveryContentItem,
  DeliveryMethod,
  MailDeliveryContent,
  MailDeliveryContentInput,
  TemplateContent,
} from '@/models/mail-delivery/mail-delivery-content'
import {
  GetMailDeliveryHistoryParams,
  MailDeliveryHistoryResult,
  MailDeliverySendResultsResult,
} from '@/models/mail-delivery/mail-delivery-history'
import {
  ReservationDetailSalesMastersType,
  SalesDepartmentMasterType,
  SalesSubjectMasterType,
  SalesSubjectTaxType,
  SalesSubSubjectMasterType,
  SalesSubjectSetType,
} from '@/models/sales-manager/sales'
import { FormValue as SalesSubjectFormValue } from '@/components/organisms/settings/sales-manager/subject-detail-modal'
import {
  AlignmentReservationInput,
  NewReservationInput,
  ReservationForSelfCheckinInput,
  SpaceReservation,
  UpdateSpaceReservationInput,
} from '@/models/space-manager'
import { AccountInfo, ApiCredential, Device, LockSetting, LockType, RoomKindType } from '@/models/lock-setting'
import { AdminAccount, AdminAccountUpdateParameter } from '@/models/admin-account'
import * as SalesInvoice from '@/models/sales-invoice'
import { DashboardSalesFilterType, DashboardSalesType } from '@/models/sales-manager/dashboard'
import { ListType } from '@/components/pages/setting/guest-room/key-number'
import * as SalesAccountingModel from '@/models/sales-manager/sales-accounting'
import { GuestRoomFloorType } from '@/models/guest-room-floor'
import { GuestRoomNumberType } from '@/models/guest-room-number'
import { ReservationNote, ReservationNoteCategory } from '@/models/reservation/reservation-note'
import { CustomerTag, CustomerTagMasterType } from '@/models/customer-manager/customer-tag'
import type { PriceRank, PriceRankInput } from '@/models/room-rate/price-rank'
import type { RateCalendarBulkInput, RateCalendarEntry, RateCalendarUpdateInput } from '@/models/room-rate/rate-calendar'
import type { BaseRateFetchParams, BaseRateRow, BaseRateUpdateInput } from '@/models/room-rate/base-rate'
import type { RoomRateDisplayFetchParams, RoomRateDisplayResponse } from '@/models/room-rate/room-rate-display'
import { PaymentSettingType, PaymentTerminalSettingType } from '@/models/payment-setting'
import { SearchCustomerListParams } from '@/models/customer-manager/customer'
import type {
  RestaurantCancellationConfirmRequest,
  RestaurantCancellationHistoryResponse,
  RestaurantColumnSettingUpdateRequest,
  RestaurantCreateMealSpaceRequest,
  RestaurantDeleteMealSpaceDetailRequest,
  RestaurantDisplaySubjectsResponse,
  RestaurantDisplaySubject,
  RestaurantDisplaySubjectOrderUpdateRequest,
  RestaurantHighlightSettingUpdateRequest,
  RestaurantMealMemoUpdateRequest,
  RestaurantMealSpaceCandidateQuery,
  RestaurantMealSpaceCandidateResponse,
  RestaurantOverviewQuery,
  RestaurantOverviewSnapshot,
  RestaurantUpdateMealSpaceDetailRequest,
} from '@/models/restaurant-manager'
import { fingerprint } from '@/utils/fingerprint'
import { CardReaderCommand, RoomInfoType, SelfCheckinCardReaderType } from '@/models/self-checkin/card-reader'
import type { SalesSubjectTaxPayload } from '@/components/organisms/settings/sales-manager/subject-tax-detail-modal'
import { KeywordGroup } from '@/models/reservation/keyword-group'
import { TimeSelectionIndex, TimeSelectionSubmitData } from '@/models/custom-checkin-time-selection'
import * as CustomExport from '@/models/reservation/custom-export'
import { EditRoomType } from '@/hooks/use-edit-guest-rooms'
import * as AccountsRreceivableMananger from '@/models/accounts-receivable-manager'
import { LatestVersionKey } from '@/utils/force-version-up'
import { SortKeyType } from '@/components/molecules/sortable-table-header'
import { PaymentResultByCash, PaymentResultByCredit, SelectedReservationAndRooms } from '@/models/self-checkin'
import { DailySummary, MonthlySummary } from '@/models/sales-manager/detail'
import { ChannelCodeType } from '@/models/reservation-channel-code'
import * as MarketSegmentModel from '@/models/reservation-market-segment'
import type { ReservationTaskSetting, ReservationTaskSettingParams } from '@/models/reservation/reservation-task-setting'
import type {
  ReservationTaskListParams,
  ReservationTaskListResponse,
  ReservationTaskUnhandledTotalResponse,
  ReservationTaskUpdateParams,
} from '@/models/reservation/reservation-task'
import type { HotelPmsPermissions } from '@/models/hotel-pms-permission'
import type { EmployeeHotel } from '@/models/employee-hotel'
import { SuccessMailType } from '@/hooks/use-self-check-in-state'
import * as selfCheckoutSettingModel from '@/models/self-checkout/setting'
import type { SearchSetting } from '@/models/search-setting'
import * as selfCheckoutModel from '@/models/self-checkout'
import { NpsSummaryResponse, NpsSummaryParams } from '@/models/survey/nps-summary'
import { SurveyStatisticsResponse, SurveyStatisticsParams } from '@/models/survey/statistics'
import { SurveyAnswersResponse, SurveyAnswersParams, SurveyCsvDownloadParams } from '@/models/survey/answers'
import { SaleSubjectCsvUploadSettingType } from '@/libs/sales-subjects'
import { DashboardTotalResponse } from '@/models/dashboard/dashboard-total'
import { DashboardReservationResponse } from '@/models/dashboard/dashboard-reservation'
import type { SelfCheckinSettingResponse } from '@/models/self-checkin/setting'
import { OutputCompleteEvent } from './vescajs'
import type { ReportFormType } from '@/features/report-manager/report-manager-slice'
import type { ReceiptProvisoItem, ProvisoInput } from '@/models/hotel/receipt-proviso'
import type {
  FetchPushSubscriptionCtiLineSettingsParams,
  FetchPushSubscriptionCtiLineSettingsResponse,
  PushSubscriptionRequest,
  UpdatePushSubscriptionCtiLineSettingsParams,
} from '@/models/push-notification'
import { UpdateGuestParams } from '@/models/guest-input'
import I18n from '@/i18n'
import {
  canEditScreen,
  getScreenKeyByPathname,
  getStoredHotelPmsPermissionSnapshot,
  HotelPmsReadOnlyGuardCancelledError,
  shouldSkipHotelPmsWriteGuard,
} from '@/libs/hotel-pms-permission'
import type { DiscountARRule, DiscountARRuleInputParams } from '@/models/discount-ar-rule'
import type { UploadKey } from '@/models/file-upload'
import type { PostClosingSalesAdjustmentParams, SelectablePaymentBusinessDatesResponse } from '@/models/post-closing-sales-adjustment'
import type { GetSelfCheckinSelfCheckinBasicInfoResponse } from '@/models/self-checkin/plugin'
import type { MfaChallengeType } from '@/contexts/auth'
import { HotelBasicInfo, HotelBasicInfoInput, HotelReceiptInfoInput } from '@/models/hotel/basic-info'

const ourRequest = axios.CancelToken.source()
const apiStore: {
  lastMustShowError?: AxiosError
  lastMustShowErrorTimer: NodeJS.Timeout | undefined
} = {
  lastMustShowError: undefined,
  lastMustShowErrorTimer: undefined,
}
axiosRetry(axios, {
  retries: Infinity,
  retryDelay: () => 3000,
  retryCondition: () => typeof navigator !== 'undefined' && navigator.onLine === false,
})

export type HttpParams = {
  url: string
  method: 'get' | 'post' | 'put' | 'delete' | 'patch'
  headers?: Record<string, any>
  params?: any
  data?: any
  responseType?: any
  paramsSerializer?: (params: any) => string
  /** GET のみ有効。CSV/PDF 等「更新系と同様にスタッフガードしたい」リクエストで true */
  staffMutationTreatAsWrite?: boolean
}

export async function http<T = any>(config: HttpParams): Promise<AxiosResponse<T>> {
  try {
    if (onlyForDev) {
      const mockResult = await searchMockResult(config)

      if (mockResult) return mockResult
    }

    const { params, data, responseType, paramsSerializer, staffMutationTreatAsWrite } = config
    const axiosStaffGuard = staffMutationTreatAsWrite === true ? ({ staffMutationTreatAsWrite: true } as const) : {}
    const headers = config.headers || {}
    headers['x-front-path'] = window?.location?.pathname || 'N/A'
    headers['x-version'] = (env('APP_TAG') as string) || 'N/A'
    headers['x-fingerprint'] = fingerprint

    const Authorization = getIdToken()
    if (Authorization) headers['Authorization'] = Authorization

    const isWriteMethod = ['post', 'put', 'patch', 'delete'].includes(config.method)
    if (isWriteMethod && typeof window !== 'undefined' && !shouldSkipHotelPmsWriteGuard()) {
      const snapshot = getStoredHotelPmsPermissionSnapshot()
      const currentPath = window.location.pathname || '/'
      const screenKey = getScreenKeyByPathname(currentPath)
      const permissionsReady = Boolean(snapshot?.loaded === true && snapshot.error === false && snapshot.permissions)

      // スナップショット不在・権限未取得は fail-safe（書き込みブロック）
      if (!snapshot || !permissionsReady) {
        alert(I18n.t('HotelPms.Error.contextMissing'))
        throw new HotelPmsReadOnlyGuardCancelledError()
      }

      // screenKey が未判定のパスでは誤拒否を避けるため、読み取り専用判定もスキップ
      if (screenKey && !canEditScreen(snapshot.permissions, screenKey, 'deny')) {
        alert(I18n.t('HotelPms.Error.readOnly'))
        throw new HotelPmsReadOnlyGuardCancelledError()
      }
    }

    axios.interceptors.response.use(response => {
      const latestVersion = response.headers['x-hotel-version']
      if (!latestVersion) {
        return response
      }
      sessionStorage.setItem(LatestVersionKey, latestVersion)
      return response
    })

    switch (config.method) {
      case 'get':
        return await axios.get<T>(config.url, {
          headers,
          params,
          data,
          responseType,
          cancelToken: ourRequest.token,
          paramsSerializer,
          withCredentials: true,
          ...axiosStaffGuard,
        })
      case 'post':
        return await axios.post<T>(config.url, data, {
          headers,
          params,
          cancelToken: ourRequest.token,
          withCredentials: true,
          ...axiosStaffGuard,
        })
      case 'patch':
        return await axios.patch<T>(config.url, data, {
          headers,
          params,
          cancelToken: ourRequest.token,
          withCredentials: true,
          ...axiosStaffGuard,
        })
      case 'put':
        return await axios.put<T>(config.url, data, {
          headers,
          params,
          cancelToken: ourRequest.token,
          withCredentials: true,
          ...axiosStaffGuard,
        })
      case 'delete':
        return await axios.delete<T>(config.url, {
          headers,
          params,
          data,
          cancelToken: ourRequest.token,
          paramsSerializer,
          withCredentials: true,
          ...axiosStaffGuard,
        })
      default:
        throw new Error('not reachable')
    }
  } catch (e) {
    const error = e as AxiosError
    const status = error?.response?.status
    const mustShow = error?.response?.data?.mustShow

    if (status === 401) {
      ourRequest.cancel()
      auth0.signout()
      return axios.request(error?.config)
    }

    if (mustShow) {
      if (isEqual(apiStore.lastMustShowError, error.response?.data)) return Promise.reject(error)
      if (apiStore.lastMustShowErrorTimer) clearTimeout(apiStore.lastMustShowErrorTimer)

      apiStore.lastMustShowError = error.response?.data
      apiStore.lastMustShowErrorTimer = setTimeout(() => {
        apiStore.lastMustShowError = undefined
      }, 3000)

      errorHandler(error, { mustShow: true })
    }

    return Promise.reject(error)
  }
}

function getIdToken(): string | null {
  return localStorage.getItem('idToken')
}

export const pmsAuthMfaChallenge = (idToken: string) => {
  return http<MfaChallengeType>({
    method: 'post',
    url: `${env('BASE_API_V3')}/pms/auth/mfa/challenge`,
    headers: { authorization: idToken },
  }).then(results => results.data)
}

export const pmsAuthMfaVerify = (idToken: string, data: { otp: string }) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/pms/auth/mfa/verify`,
    data,
    headers: { authorization: idToken },
  })
}

export const pmsAuthMfaSend = (idToken: string) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/pms/auth/mfa/send`,
    headers: { authorization: idToken },
  })
}

export const pmsAuthMfaSendToHotel = (idToken: string) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/pms/auth/mfa/send-to-hotel`,
    headers: { authorization: idToken },
  })
}

export const fetchSelfCheckinPrint = (params: {
  hotelId: string
  checkinId: string
  reservationIdWithAssignIds: SelectedReservationAndRooms[]
}) => {
  return http({
    method: 'get',
    url: `${env('BASE_API_V3')}/self-checkin/print`,
    params,
    paramsSerializer: params => qs.stringify(params, { arrayFormat: 'indices' }),
  }).then(results => results.data)
}

/**
 * 未読数情報 取得API

 */
export const fetchCheckinUnreadTotal = () => {
  return http<Aipass.GetCheckinUnreadTotalResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-checkin-unread-total`,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * 会社情報 取得API
 */
export const fetchAdminAccount = () => {
  return http<AdminAccount>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-account`,
  })
    .then(results => results.data)
    .catch(e => {
      const status = e?.response?.status
      if (status === 403) {
        alert('IPアドレスが許可されていないため、ログインできませんでした')
        auth0.signout()
      }
      console.error(e)
    })
}

/**
 * 会社情報 更新API
 */
export const updateAdminAccount = (data: AdminAccountUpdateParameter) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/admin-account`,
    data,
  })
}

/**
 * 館内案内情報 基本情報取得API
 */
export const fetchFacilityBasicInfo = () => {
  return http<{ basicInfo: HotelBasicInfo }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-facility`,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * 館内案内情報 基本情報更新API
 */
export const updateFacilityBasicInfo = (data: HotelBasicInfoInput) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/admin-facility`,
    data,
  })
}

export const updateFacilityReceiptInfo = (data: HotelReceiptInfoInput) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/admin-facility-receipt`,
    data,
  })
}

/**
 * 機器送信履歴情報取得API
 */
export const fetchDeviceSendHistory = (printerSettingIds: string[]) => {
  const params = { printerSettingIds }
  const Authorization = getIdToken()
  const headers = { Authorization }
  return http<{ deviceSendHistory: DeviceSendHistory[] }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/device-send-history`,
    headers,
    params,
  })
    .then(results => results.data?.deviceSendHistory)
    .catch(e => console.error(e))
}

/**
 * 機器送信履歴情報更新API
 */
export const updateDeviceSendHistory = (data: { deviceSendHistoryId: string; errorMessage: string | null }) => {
  const Authorization = getIdToken()
  const headers = { Authorization }
  return http<Aipass.PutDeviceSendHistoryResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/device-send-history`,
    headers,
    data,
  })
}

/**
 * チェックイン情報一覧 取得API
 */
export const fetchAdminCheckinList = (
  page = 0,
  limit,
  name,
  nameKana,
  searchReservationId,
  roomNumber,
  searchBeginDate: string | undefined,
  searchEndDate: string | undefined,
  sortKey: SortKeyType,
) => {
  const params = {
    page,
    limit,
    name,
    nameKana,
    searchReservationId,
    roomNumber,
    searchBeginDate: searchBeginDate ? dayjs(searchBeginDate).startOf('day').unix() : null,
    searchEndDate: searchEndDate ? dayjs(searchEndDate).endOf('day').unix() : null,
    sortKey,
  }

  return http<Aipass.GetAdminCheckinListResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-checkin-list`,
    params,
    paramsSerializer: params => qs.stringify(params),
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * チェックイン情報一覧 更新API
 */
export const updateAdminCheckinList = (
  data:
    | {
        checkinId: string
        frontSupported: number | boolean
        mode: 'frontSupported'
      }
    | {
        checkinId: string
        mode: 'checkinRead' | 'stayRead' | 'checkoutRead' | 'delete'
      }
    | {
        id: string // hotel_cuicin_user_id
        mode: 'customerRead'
      },
) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/admin-checkin-list`,
    data,
  }).catch(e => {
    if (data.mode === 'frontSupported') {
      throw e
    }
    console.error(e)
  })
}

/**
 * 予約既読 更新API
 */
export const updateAdminReservationRead = reservationId => {
  const data = {
    reservationId,
    mode: 'reservationRead',
  }

  return http<Aipass.PutAdminReservationResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/admin-reservation`,
    data,
  }).then(results => results.data)
}

/**
 * チェックイン情報 取得API
 */
export const fetchAdminCheckin = checkinId => {
  return http<Aipass.GetAdminCheckinResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-checkin?checkinId=${checkinId}`,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * チェックイン情報 更新API
 */
export const updateAdminCheckin = (data = {}) => {
  return http<Aipass.PutAdminCheckinResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/admin-checkin`,
    data,
  }).then(results => results.data)
}

/**
 * チェックイン身分証を更新するAPI
 */
export const updateCheckinIdentification = (_identification: IdentificationType[]) => {
  const identification = _identification
  _identification.map((v, index) => {
    identification[index]['isDriversLicense'] = Number(v.isDriversLicense)
  })
  const data = { identification }
  return http<Aipass.PostAdminCheckinIdentificationResponse>({
    method: 'post',
    url: `${env('BASE_API_V3')}/admin-checkin-identification`,
    data,
  }).then(results => results.data)
}

/**
 * チェックイン身分証を削除するAPI
 */
export const deleteCheckinIdentification = (deleteIdentifications: string[]) => {
  const params = { deleteIdentifications }
  return http<Aipass.PostAdminCheckinIdentificationResponse>({
    method: 'delete',
    url: `${env('BASE_API_V3')}/admin-checkin-identification`,
    params,
  }).then(results => results.data)
}

/**
 * チェックインレビュー情報を取得するAPI
 */
export const fetchCheckinReview = checkinId => {
  const params = {
    checkinId,
  }

  return http<Aipass.GetCheckinReviewResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-checkin-review`,
    params,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * チェックアウト時のレビュー情報を取得するAPI
 */
export const fetchReservationReview = reservationId => {
  const params = { reservationId }

  return http<Aipass.GetAdminReservationReviewResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-reservation-review`,
    params,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * ダッシュボード情報を取得するAPI
 */
export const fetchAdminDashboard = (dateOf: Dayjs) => {
  const params = { dateOf: dateOf.format('YYYY-MM-DD') }
  return http<DashboardTotalResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/dashboard-total`,
    params,
  }).then(results => results.data)
}

export const fetchDashboardReservation = (date: string) => {
  return http<DashboardReservationResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/dashboard-reservation`,
    params: { date },
  }).then(results => results.data)
}

/**
 * ダッシュボード設定更新API
 */
export const updateDashboardSettings = (data: {
  columnSettings: Array<{
    columnName: string
    sortOrder: number
    isVisible: boolean
  }>
  filterSetting: {
    memoFilterType: 'all' | 'withMemo'
  }
}) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/dashboard-settings`,
    data,
  }).then(results => results.data)
}

/**
 * 予約メモの既読処理API
 */
export const putAdminReadReservationNote = reservationId => {
  const data = { reservationId }
  return http<Aipass.PutAdminReadReservationNoteResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/admin-read-reservation-note`,
    data,
  }).then(results => results.data)
}

/**
 * 評価の既読処理API
 */
export const putAdminReadReview = checkinId => {
  const data = { checkinId }

  return http<Aipass.PutAdminReadReviewResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/admin-read-review`,
    data,
  }).then(results => results.data)
}

/**
 * 周辺情報を取得するAPI
 */
export const fetchAroundSpots = hotelId => {
  const params = {
    hotelId,
  }

  return http<Aipass.GetAdminAroundSpotResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-around-spot`,
    params,
  })
    .then(results => results.data.aroundSpots)
    .catch(e => console.error(e))
}

/**
 * 周辺情報を取得するAPI
 */
export const updateAroundSpots = aroundSpots => {
  const data = { aroundSpots }
  return http<Aipass.PostAdminAroundSpotResponse>({
    method: 'post',
    url: `${env('BASE_API_V3')}/admin-around-spot`,
    data,
  }).then(results => results.data)
}

/**
 * 予約情報を一覧取得するAPI
 */
export const fetchReservation = (
  params: EntireSearchContentType & {
    page: number
    limit: number
    sortKey?: SortKeyType
  },
) => {
  return http<{ total: number; hasNext: Boolean; reservations: SearchReservationList[] }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-reservation`,
    params,
    paramsSerializer: params => qs.stringify(params),
  })
    .then(results => results.data)
    .catch(e => {
      console.error(e)
      return { total: 0, hasNext: false, reservations: [] as SearchReservationList[] }
    })
}

export const fetchReservationSuggest = (tel: string) => {
  return http<{ reservations: { id: string; reservationId: string; checkinId: string | undefined; approvedStatus: ApprovedStatusType }[] }>(
    {
      method: 'get',
      url: `${env('BASE_API_V3')}/admin-reservation/suggest`,
      params: { tel },
    },
  ).then(results => results.data)
}

export const getLinkedReservationsBySmaregi = () => {
  return http<SearchReservationEachRoom[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-reservation/staying`,
  })
    .then(results => results.data)
    .catch(e => {
      console.error(e)
      return [] as SearchReservationEachRoom[]
    })
}
export const getLinkedReservationsBySpace = (params: { usageDay: string }) => {
  return http<SearchReservationEachRoom[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-reservation/staying`,
    params,
    paramsSerializer: params =>
      qs.stringify({
        assignStatuses: [ApprovedStatus.Reservation, ApprovedStatus.Checkin, ApprovedStatus.Stay],
        ...params,
      }),
  })
    .then(results => results.data)
    .catch(e => {
      console.error(e)
      return [] as SearchReservationEachRoom[]
    })
}
/**
 * 予約情報を取得するAPI
 */
export const fetchReservationDetail = ({ reservationId }: { reservationId: string }) => {
  return http<Aipass.GetAdminReservationResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-reservation/detail`,
    params: { reservationId },
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * 関連する予約情報を取得するAPI
 */
export const fetchReservationLink = (params: { beginDate: string; endDate: string; page: number }) => {
  return http<{ total: number; hasNext: boolean; reservations: any[] }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-reservation-link`,
    params,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * 予約情報を更新するAPI
 */
export const putAdminReservation = (data = {}) => {
  return http<Aipass.PutAdminReservationResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/admin-reservation`,
    data,
  }).then(results => results.data)
}

export const putAdminReservationUser = (
  reservationId: string,
  data: {
    reservationUser: ReservationUserType
    reservationGuest: ReservationGuestType | null
  },
) => {
  return http<Aipass.PutAdminReservationUserResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/admin-reservation-user`,

    data: { ...data, reservationId },
  }).then(results => results.data)
}

/**
 * 予約ステータスを更新するAPI
 */
export const putAdminReservationApprovedStatus = (data: { reservationIds: string[]; approvedStatus: ApprovedStatusType }) => {
  return http<Aipass.PutAdminReservationApprovedStatusResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/admin-reservation-approved-status`,
    data,
  })
}

/**
 * CSV予約情報を登録するAPI
 */
export const updateReservationCsv = (fileName: string, reservationData: ReservationData[]): Promise<AxiosResponse<void>> => {
  const data = { fileName, reservationData, version: 3 }
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/admin-reservation-csv`,
    data,
  })
}

/**
 * CSV予約情報を登録するAPI
 */
export const insertTicketCsv = (
  fileName: string,
  ticketData: { reservationId: string | null; exchangeCode: string | null; ticketName: string | null }[],
): Promise<AxiosResponse<void>> => {
  const data = { fileName, ticketData }
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/admin-ticket-csv`,
    data,
  })
}

/**
 * 客室情報を取得するAPI
 */
export const fetchGuestRoomForHotel = () => {
  return http<Aipass.GetAdminGuestRoomResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-guest-room`,
  })
    .then(results => results.data.guestRoomInfo)
    .catch(e => console.error(e))
}

/**
 * 客室,鍵情報を取得するAPI
 */
export const fetchGuestRoomKey = () => {
  return http<Aipass.GetAdminGuestRoomKeyResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/smart-lock/assign-key/rooms`,
  })
    .then(results => results.data)
    .catch(e => {
      console.error(e)
      return []
    })
}

/**
 * 鍵情報を更新するAPI
 */
export const updateGuestRoomKey = (guestRoomKeys: ListType[]) => {
  return http<Aipass.PutAdminGuestRoomKeyResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/smart-lock/assign-key/rooms`,
    data: { guestRoomKeys },
  })
}

/**
 * 予約情報の客室情報を取得するAPI
 */
export const fetchGuestRoomForReservation = (reservationId: string) => {
  return http<GuestRoomAssignResponseType>({
    method: 'get',
    url: `${env('BASE_API_V3')}/guest-room-assign`,
    params: { reservationId },
  })
    .then(results => results.data.rooms)
    .catch(e => console.error(e))
}

export const fetchGuestRoomIndicator = (date: Dayjs) => {
  return http<GuestRoomIndicator.RoomIndicatorResponseType>({
    method: 'get',
    url: `${env('BASE_API_V3')}/room-manager/indicator`,
    params: { targetDate: date.format('YYYY-MM-DD') },
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * GET guest-room
 * 滞在中以外の部屋タイプと部屋番号の取得(客室管理プラグイン無)
 * @param checkinId
 */
export const fetchGuestRoomSelectable = () => {
  return http<Aipass.GetGuestRoomSelectableResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/guest-room-selectable`,
  })
    .then(results => results.data.guestRooms)
    .catch(e => console.error(e))
}

/**
 * GET guest-room
 * 滞在中以外の部屋タイプと部屋番号の取得(客室管理プラグイン有)
 * @param checkinId
 */
export const fetchGuestRoomSelectableHasRoomManagerPlugin = (
  assignCheckinDate: string,
  assignCheckoutDate: string,
  reservationId?: string,
) => {
  const params: { assignCheckinDate: string; assignCheckoutDate: string; reservationId?: string } = {
    assignCheckinDate: dayjs(assignCheckinDate).hour(12).format('YYYY-MM-DD'),
    assignCheckoutDate: dayjs(assignCheckoutDate).hour(12).format('YYYY-MM-DD'),
  }

  if (reservationId) {
    params.reservationId = reservationId
  }

  return http<Aipass.GetRoomManagerGuestRoomSelectableResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/room-manager/guest-room-selectable`,
    params,
  })
    .then(results => results.data.guestRooms)
    .catch(e => console.error(e))
}

/**
 * 客室管理 全部屋アサイン状況取得API
 * @param data
 */
export const fetchGuestRoomAssignStatus = (type, beginDate = '', endDate = '') => {
  const params = {
    type,
    beginDate: beginDate ? dayjs(beginDate).format('YYYY-MM-DD') : undefined,
    endDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : undefined,
    with_unspecified: 1,
  }
  return http<Aipass.GetRoomManagerGuestRoomAssignStatusResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/room-manager/guest-room-assign-status`,
    params,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

export const updateGuestRoomAssignStatus = (data: { reservationId: string; guestRoomAssignIds: string[]; status: ApprovedStatusType }) => {
  return http<Aipass.GetRoomManagerGuestRoomAssignStatusResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/room-manager/guest-room-assign-status`,
    data,
  })
}

/**
 * 客室管理 サイトコントローラー在庫の一括更新API
 * PMSの在庫数で、連携中のサイトコントローラー側の在庫を上書きする
 */
export const bulkUpdateSiteControllerStock = ({ beginDate, endDate }: SiteControllerStockBulkUpdateRequestType) => {
  return http<void>({
    method: 'post',
    url: `${env('BASE_API_V3')}/admin-reservation-stock/sync`,
    data: { dateFrom: beginDate, dateTo: endDate },
  }).then(results => results.data)
}

/**
 * 客室管理 準備中の一括登録API
 * 指定期間・部屋タイプの全客室を準備中にする（予約済みの客室はサーバー側でスキップされる）
 */
export const bulkCreateGuestRoomPreparation = (data: PreparationBulkCreateRequestType) => {
  return http<void>({
    method: 'post',
    url: `${env('BASE_API_V3')}/room-manager/guest-room-preparation/bulk`,
    data,
  }).then(results => results.data)
}

export const fetchAdminReservationStock = (dateFrom, dateTo) => {
  return http<{
    stocks: SiteControllerStockType.SiteControllerStockType
    totalRemainingCountEachDate: SiteControllerStockType.TotalRemainingCountEachDateType
  }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-reservation-stock`,
    headers: { Authorization: getIdToken() },
    params: { dateFrom, dateTo },
  }).then(results => results.data)
}

/**
 * 客室情報を登録するAPI
 */
export const updateGuestRoom = (guestRoomTypes, guestRoomOtaCodes, guestRooms, guestRoomFloors) => {
  const data = { guestRoomTypes, guestRoomOtaCodes, guestRooms, guestRoomFloors }
  return http<Aipass.PostAdminGuestRoomResponse>({
    method: 'post',
    url: `${env('BASE_API_V3')}/admin-guest-room`,
    data,
  }).then(results => results.data)
}

/**
 * 各詳細からアサイン部屋保存処理
 */

export const updateAllGuestRoomAssign = (
  reservation: AdminReservationResponseType['reservations'],
  editGuestRooms: GuestRoomAssignResponseType['rooms'],
) => {
  const data = {
    reservationId: reservation.reservationId,
    checkinDate: dayjs(reservation.checkinDate).hour(12).format('YYYY-MM-DD'),
    checkoutDate: reservation.checkoutDate ? dayjs(reservation.checkoutDate).hour(12).format('YYYY-MM-DD') : null,
    rooms: editGuestRooms.map(room => {
      return {
        guestRoomAssignId: room.guestRoomAssignId ?? null,
        guestRoomId: room.guestRoomId ?? null,
        assignGuestName: room.assignGuestName ?? null,
        roomTypeName: room.typeNameJa ?? null,
        roomNumber: room.roomNumber ?? null,

        paxChildA: room.paxChildA ?? null,
        paxChildB: room.paxChildB ?? null,
        paxChildC: room.paxChildC ?? null,
        paxChildD: room.paxChildD ?? null,
        paxChildE: room.paxChildE ?? null,
        paxChildF: room.paxChildF ?? null,
        paxChildOther: room.paxChildOther ?? null,
        paxFemale: room.paxFemale ?? null,
        paxMale: room.paxMale ?? null,
        pax: room.pax ?? null,
        checkinDate: room.assignCheckinDate ? dayjs(room.assignCheckinDate).hour(12).format('YYYY-MM-DD') : null,
        checkoutDate: room.assignCheckoutDate ? dayjs(room.assignCheckoutDate).hour(12).format('YYYY-MM-DD') : null,
      }
    }),
  }
  return http<Aipass.PostGuestRoomAssignResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/guest-room-assign`,
    data,
  }).then(results => results.data)
}

export const updateGuestRoomAssign = (editGuestRooms: GuestRoomAssignResponseType['rooms']) => {
  const data = {
    rooms: editGuestRooms.map(room => {
      return {
        guestRoomAssignId: room.guestRoomAssignId ?? null,
        assignGuestName: room.assignGuestName ?? null,

        paxChildA: room.paxChildA ?? null,
        paxChildB: room.paxChildB ?? null,
        paxChildC: room.paxChildC ?? null,
        paxChildD: room.paxChildD ?? null,
        paxChildE: room.paxChildE ?? null,
        paxChildF: room.paxChildF ?? null,
        paxChildOther: room.paxChildOther ?? null,
        paxFemale: room.paxFemale ?? null,
        paxMale: room.paxMale ?? null,
        pax: room.pax ?? null,
      }
    }),
  }
  return http<Aipass.PostGuestRoomAssignResponse>({
    method: 'patch',
    url: `${env('BASE_API_V3')}/guest-room-assign`,
    data,
  }).then(results => results.data)
}

/**
 * 宿泊者ゲストを削除する
 */
export const deleteGuest = (checkinId, assignGroupId = '') => {
  const params = { checkinId, assignGroupId }
  return http<Aipass.DeleteGuestRoomAssignResponse>({
    method: 'delete',
    url: `${env('BASE_API_V3')}/guest-room-assign`,
    params,
  }).then(results => results.data)
}

/**
 * チェックインと予約データの紐付け
 */
export const insertAdminReservationCheckin = (data: { checkinId: string; reservationIds: string[] }) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/admin-reservation-checkin`,
    data,
  })
}

/**
 * チェックインと予約データの紐付け解除
 */
export const deleteAdminReservationCheckin = (params: { checkinId: string; reservationId: string }) => {
  return http({
    method: 'delete',
    url: `${env('BASE_API_V3')}/admin-reservation-checkin`,
    params,
  })
}

/**
 * 引き継ぎ用の予約メモ情報を取得するAPI
 * @param reservationId
 */
export const fetchReservationPastNote = ({ customerId, checkoutDate }: { customerId: string; checkoutDate: string }) => {
  const params = { customerId, checkoutDate }
  return http<ReservationNote[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-reservation-note-past`,
    params,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * 予約メモ情報を取得するAPI
 * @param reservationId
 */
export const fetchReservationNote = reservationId => {
  const params = { reservationId }
  return http<{ notes: ReservationNote[] }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-reservation-note`,
    params,
  })
    .then(results => results.data.notes)
    .catch(e => {
      console.error(e)
      return []
    })
}

/**
 * 予約メモ情報を更新するAPI
 * @param reservationId
 * @param notes
 */
export const updateReservationNote = (reservationId, notes, checkinId?: string) => {
  const data = { reservationId, notes, checkinId }

  return http<Aipass.PostAdminReservationNoteResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/admin-reservation-note`,
    data,
  }).then(results => results.data)
}

export const fetchRestaurantOverview = (query: RestaurantOverviewQuery) => {
  return http<RestaurantOverviewSnapshot>({
    method: 'get',
    url: `${env('BASE_API_V3')}/restaurant-meal-overview`,
    params: query,
    paramsSerializer: params => qs.stringify(params, { arrayFormat: 'brackets' }),
  }).then(results => results.data)
}

export const fetchRestaurantCancellationHistory = () => {
  return http<RestaurantCancellationHistoryResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/restaurant-meal-overview/cancellations`,
  }).then(results => results.data)
}

export const fetchRestaurantMealSpaceCandidates = (query: RestaurantMealSpaceCandidateQuery) => {
  return http<RestaurantMealSpaceCandidateResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/restaurant-meal-overview/reservations/${query.reservationId}/space-reservation-candidates`,
    params: { date: query.date },
  }).then(results => results.data)
}

export const updateRestaurantColumnSetting = (data: RestaurantColumnSettingUpdateRequest) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/restaurant-meal-overview/display-columns`,
    data,
  }).then(results => results.data)
}

export const updateRestaurantMealMemo = ({ memo, reservationId }: RestaurantMealMemoUpdateRequest) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/restaurant-meal-overview/reservations/${reservationId}/meal-note`,
    data: { memo },
  }).then(results => results.data)
}

export const createRestaurantMealSpaceReservation = ({ reservationId, ...data }: RestaurantCreateMealSpaceRequest) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/restaurant-meal-overview/reservations/${reservationId}/space-reservations`,
    data,
  }).then(results => results.data)
}

export const updateRestaurantMealSpaceReservationDetail = ({ spaceUsageDetailId, ...data }: RestaurantUpdateMealSpaceDetailRequest) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/restaurant-meal-overview/space-reservation-details/${spaceUsageDetailId}`,
    data,
  }).then(results => results.data)
}

export const deleteRestaurantMealSpaceReservationDetail = ({ spaceUsageDetailId }: RestaurantDeleteMealSpaceDetailRequest) => {
  return http({
    method: 'delete',
    url: `${env('BASE_API_V3')}/restaurant-meal-overview/space-reservation-details/${spaceUsageDetailId}`,
  }).then(results => results.data)
}

export const confirmRestaurantCancellation = (data: RestaurantCancellationConfirmRequest) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/restaurant-meal-overview/cancellations/confirm`,
    data,
  }).then(results => results.data)
}

export const fetchRestaurantHighlightRules = () => {
  return http<RestaurantHighlightSettingUpdateRequest>({
    method: 'get',
    url: `${env('BASE_API_V3')}/restaurant-meal-overview/settings/highlight-rules`,
  }).then(results => results.data)
}

export const fetchRestaurantDisplaySubjects = () => {
  return http<RestaurantDisplaySubjectsResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/restaurant-meal-overview/settings/display-subjects`,
  }).then(results => results.data.displaySubjects)
}

export const updateRestaurantHighlightSetting = (data: RestaurantHighlightSettingUpdateRequest) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/restaurant-meal-overview/settings/highlight-rules`,
    data,
  }).then(results => results.data)
}

export const createRestaurantDisplaySubject = (data: RestaurantDisplaySubject) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/restaurant-meal-overview/settings/display-subjects`,
    data,
  }).then(results => results.data)
}

export const updateRestaurantDisplaySubject = (subjectId: string, data: RestaurantDisplaySubject) => {
  return http({
    method: 'patch',
    url: `${env('BASE_API_V3')}/restaurant-meal-overview/settings/display-subjects/${subjectId}`,
    data,
  }).then(results => results.data)
}

export const updateRestaurantDisplaySubjectOrder = (data: RestaurantDisplaySubjectOrderUpdateRequest) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/restaurant-meal-overview/settings/display-subjects/order`,
    data,
  }).then(results => results.data)
}

/**
 * チェックイン案内メッセージを更新するAPI
 * @param reservationId
 * @param memo
 */
export const updateReservationCheckinGuidance = (data: { reservationId: string; memo: string }): Promise<void> => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/admin-reservation-checkin-guidance`,
    data,
  }).then(() => undefined)
}

/**
 * お知らせ情報を取得するAPI
 */
export const fetchAdminNotice = () => {
  return http<Aipass.GetNoticeResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/notice`,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * 通知センター表示API
 */
export const checkNoticeCenter = () => {
  return http<Aipass.PutNoticeResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/notice`,
  })
}

/**
 * お知らせ既読API
 * @param id
 */
export const readAdminNotice = (id: string) => {
  return http<Aipass.PutNoticeResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/notice/${id}`,
  })
}

/**
 * プラグイン加入対象かどうかの確認
 */
export const fetchPlugins = () => {
  return http<Aipass.GetPluginResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/plugin`,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * 客室管理プラグインの部屋アサインAPI
 */
export const putRoomManagerPluginGuestRoomAssign = ({
  checkinDate,
  checkoutDate,
  editGuestRooms,
  reservationTel,
  reservationName,
  reservationId,
  roomsInPreparation,
  excludeStockCheck,
  validationOnly,
  skipUserDataUpdate,
}: {
  checkinDate: moment.Moment
  checkoutDate: moment.Moment
  editGuestRooms: Array<EditRoomType>
  reservationTel: string
  reservationName: string
  reservationId?: string
  roomsInPreparation?: Array<EditRoomType>
  excludeStockCheck?: boolean
  validationOnly?: boolean
  skipUserDataUpdate?: boolean
}): Promise<{ reservationId: string; reservationUuid: string }> => {
  // 部屋アサイン情報を作成
  const rooms = editGuestRooms.map(editGuestRoom => {
    return {
      guestRoomAssignId: editGuestRoom.guestRoomAssignId ?? null,
      guestRoomId: editGuestRoom.guestRoomId ?? null,
      roomTypeName: editGuestRoom.typeNameJa ?? null,
      checkinDate: editGuestRoom.assignCheckinDate ? dayjs(editGuestRoom.assignCheckinDate).format('YYYY-MM-DD') : null,
      checkoutDate: editGuestRoom.assignCheckoutDate ? dayjs(editGuestRoom.assignCheckoutDate).format('YYYY-MM-DD') : null,
    }
  })

  const data = {
    checkinDate: checkinDate.format('YYYY-MM-DD'),
    checkoutDate: checkoutDate.format('YYYY-MM-DD'),
    reservationId,
    reservationTel,
    reservationName,
    rooms,
    excludeStockCheck,
    validationOnly,
    skipUserDataUpdate,
  }

  if (roomsInPreparation?.length) {
    data['roomsInPreparation'] = roomsInPreparation.map(editGuestRoom => {
      return {
        guestRoomAssignId: editGuestRoom.guestRoomAssignId ?? null,
        guestRoomId: editGuestRoom.guestRoomId ?? null,
        roomTypeName: editGuestRoom.typeNameJa ?? null,
        checkinDate: editGuestRoom.assignCheckinDate ? dayjs(editGuestRoom.assignCheckinDate).format('YYYY-MM-DD') : null,
        checkoutDate: editGuestRoom.assignCheckoutDate ? dayjs(editGuestRoom.assignCheckoutDate).format('YYYY-MM-DD') : null,
        preparationMemo: editGuestRoom.preparationMemo ?? '',
        preparationType: editGuestRoom.preparationType !== undefined ? Number(editGuestRoom.preparationType) : null,
      }
    })
  }

  return http<Aipass.PutRoomManagerGuestRoomAssignResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/room-manager/guest-room-assign`,
    data,
  }).then(results => results.data)
}

export const fetchSalesSubjectMaster = (option?: { isIncludeOption?: boolean; isIncludePlanCode?: boolean }) => {
  return http<SalesSubjectMasterType[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales-subject`,
    params: option,
  })
    .then(results => results.data)
    .catch(e => {
      console.error(e)
      return [] as SalesSubjectMasterType[]
    })
}
export const createSalesSubject = (data: SalesSubjectFormValue) => {
  return http<void>({
    method: 'post',
    url: `${env('BASE_API_V3')}/sales-manager/sales-subject`,
    data,
  })
}
export const updateSalesSubject = (id: string, data: SalesSubjectFormValue) => {
  return http<void>({
    method: 'put',
    url: `${env('BASE_API_V3')}/sales-manager/sales-subject/${id}`,
    data,
  })
}
export const deleteSalesSubject = (ids: string[]) => {
  return http<void>({
    method: 'delete',
    url: `${env('BASE_API_V3')}/sales-manager/sales-subject/bulk-delete`,
    params: { ids },
  })
}
export const importSalesSubject = (salesSubjectDepartmentData, uploadSetting: SaleSubjectCsvUploadSettingType) => {
  const data = { salesSubjectDepartmentData, uploadSetting }
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/sales-manager/sales-subject/csv`,
    data,
  })
}
export const exportSalesSubject = () => {
  return http<{ url: string }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales-subject/csv`,
    staffMutationTreatAsWrite: true,
  }).then(results => results.data)
}

export const fetchSalesSubjectSet = () => {
  return http<SalesSubjectSetType[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales-subject-set`,
  })
    .then(results => results.data)
    .catch(e => {
      console.error(e)
      return []
    })
}

export const createSalesSubjectSet = data => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/sales-manager/sales-subject-set`,
    data,
  }).then(results => results.data)
}

export const updateSalesSubjectSet = data => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/sales-manager/sales-subject-set`,
    data,
  }).then(results => results.data)
}

export const deleteSalesSubjectSet = params => {
  return http({
    method: 'delete',
    url: `${env('BASE_API_V3')}/sales-manager/sales-subject-set`,
    params,
    paramsSerializer: params => {
      return qs.stringify(params)
    },
  }).then(results => results.data)
}

export const updateSalesSubjectSetList = data => {
  return http<Aipass.GetSalesManagerSalesSubjectResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/sales-manager/sales-subject-set-list-csv`,
    data,
  }).then(results => results.data)
}

export const fetchSalesDepartment = () => {
  return http<SalesDepartmentMasterType[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales-department`,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}
export const createSalesDepartment = (data: Omit<SalesDepartmentMasterType, 'id' | 'relatedSubjectMasterCount'>) => {
  return http<void>({
    method: 'post',
    url: `${env('BASE_API_V3')}/sales-manager/sales-department`,
    data,
  })
}
export const updateSalesDepartment = (id: string, data: Omit<SalesDepartmentMasterType, 'id' | 'relatedSubjectMasterCount'>) => {
  return http<void>({
    method: 'put',
    url: `${env('BASE_API_V3')}/sales-manager/sales-department/${id}`,
    data,
  })
}
export const deleteSalesDepartment = (ids: string[]) => {
  return http<void>({
    method: 'delete',
    url: `${env('BASE_API_V3')}/sales-manager/sales-department/bulk-delete`,
    params: { ids },
  })
}

export const createSalesSubSubject = (data: Omit<SalesSubSubjectMasterType, 'id' | 'salesSubjectMasterId'>) => {
  return http<void>({
    method: 'post',
    url: `${env('BASE_API_V3')}/sales-manager/sales-sub-subject`,
    data,
  })
}
export const updateSalesSubSubject = (id: string, data: Omit<SalesSubSubjectMasterType, 'id' | 'salesSubjectMasterId'>) => {
  return http<void>({
    method: 'put',
    url: `${env('BASE_API_V3')}/sales-manager/sales-sub-subject/${id}`,
    data,
  })
}
export const deleteSalesSubSubject = (ids: string[]) => {
  return http<void>({
    method: 'delete',
    url: `${env('BASE_API_V3')}/sales-manager/sales-sub-subject/bulk-delete`,
    params: { ids },
  })
}

export const fetchSalesSubjectTax = () => {
  return http<SalesSubjectTaxType[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales-subject-tax`,
  })
    .then(results => results.data)
    .catch(e => {
      console.error(e)
      return []
    })
}

/**
 * 予約詳細画面で使う売上のマスタ・設定をまとめて取得する
 */
export const fetchReservationDetailSalesMasters = () => {
  return http<ReservationDetailSalesMastersType>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/reservation-detail-masters`,
  }).then(results => results.data)
}
export const createSalesSubSubjectTax = (data: SalesSubjectTaxPayload) => {
  return http<void>({
    method: 'post',
    url: `${env('BASE_API_V3')}/sales-manager/sales-subject-tax`,
    data,
  })
}
export const updateSalesSubSubjectTax = (id: string, data: SalesSubjectTaxPayload) => {
  return http<void>({
    method: 'put',
    url: `${env('BASE_API_V3')}/sales-manager/sales-subject-tax/${id}`,
    data,
  })
}
export const deleteSalesSubSubjectTax = (ids: string[]) => {
  return http<void>({
    method: 'delete',
    url: `${env('BASE_API_V3')}/sales-manager/sales-subject-tax`,
    params: { ids },
  })
}

export const fetchSalesAccountSubjectSet = ({
  checkinId,
  reservationId,
  salesSubjectSetId,
}: {
  checkinId?: string
  reservationId?: string
  salesSubjectSetId: string
}) => {
  const params = checkinId ? { checkinId, salesSubjectSetId } : { reservationId, salesSubjectSetId }
  return http<{ salesGroupBySalesDate: CalculatedSalesSubjectSetType[] }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales-account-subject-set`,
    params,
  }).then(results => results.data)
}

/**
 * 手入力予約向け宿泊税の計算プレビュー
 * - 保存は PUT admin-sales
 * - 精算済101の減額も update として返す（force なし）。適用前の確認は FE
 * - プランに含む × 室料精算済: create/update に salesPaymentId / salesPaymentPrice / salesAccountsReceivableMasterId を含む
 * - 適用対象0件時は OperationWarningException（404系, message: No applicable accommodation tax）
 */
export const fetchSalesAccountSubjectTax = ({ checkinId, reservationId }: { checkinId?: string; reservationId?: string }) => {
  const params: { checkinId?: string; reservationId?: string } = checkinId ? { checkinId } : { reservationId }
  return http<{ salesGroupBySalesDate: CalculatedSalesSubjectTaxType[] }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales-account-subject-tax`,
    params,
  }).then(results => results.data)
}

/**
 * 支払日時のUnixタイムスタンプを構築
 */
const buildPaymentDateTime = (date: string, hour?: string, minute?: string, defaultTime: string = '00:00'): number => {
  const time = hour && minute ? `${hour}:${minute}` : defaultTime
  return dayjs(`${date} ${time}`).unix()
}

/**
 * 売上明細検索パラメータをAPIリクエスト用のパラメータに変換
 */
const buildSalesDetailSearchParams = (
  beginDate: string,
  endDate: string,
  searchParams?: SalesDetailSearchType,
): Record<string, string | number> => {
  const params: Record<string, string | number> = {
    beginDate: beginDate === '' ? 0 : dayjs(beginDate).unix(),
    endDate: endDate === '' ? 0 : dayjs(endDate).unix(),
  }

  if (!searchParams) {
    return params
  }

  if (searchParams.unpaidType) {
    params.unpaidType = searchParams.unpaidType
  }
  // 補助科目選択時は salesSubSubjectId、科目のみのときは salesSubjectMasterId を送る
  if (searchParams.salesSubSubjectId) {
    params.salesSubSubjectId = searchParams.salesSubSubjectId
  } else if (searchParams.salesSubjectMasterId) {
    params.salesSubjectMasterId = searchParams.salesSubjectMasterId
  }
  if (searchParams.salesDiscountId) {
    params.salesDiscountId = searchParams.salesDiscountId
  }
  if (searchParams.minSalesPrice !== undefined) {
    params.minSalesPrice = searchParams.minSalesPrice
  }
  if (searchParams.maxSalesPrice !== undefined) {
    params.maxSalesPrice = searchParams.maxSalesPrice
  }
  if (searchParams.salesPaymentId) {
    params.salesPaymentId = searchParams.salesPaymentId
  }
  if (searchParams.salesAccountsReceivableMasterId) {
    params.salesAccountsReceivableMasterId = searchParams.salesAccountsReceivableMasterId
  }
  if (searchParams.discountSubjectName) {
    params.discountSubjectName = searchParams.discountSubjectName
  }
  if (searchParams.paymentMethodName) {
    params.paymentMethodName = searchParams.paymentMethodName
  }
  if (searchParams.accountsReceivableName) {
    params.accountsReceivableName = searchParams.accountsReceivableName
  }
  if (searchParams.beginPaymentDate) {
    params.beginPaymentDate = buildPaymentDateTime(
      searchParams.beginPaymentDate,
      searchParams.beginPaymentHour,
      searchParams.beginPaymentMinute,
      '00:00',
    )
  }
  if (searchParams.endPaymentDate) {
    params.endPaymentDate = buildPaymentDateTime(
      searchParams.endPaymentDate,
      searchParams.endPaymentHour,
      searchParams.endPaymentMinute,
      '23:59',
    )
  }

  return params
}

/**
 * 売上管理明細の取得
 */
export const fetchSalesDetailList = (
  beginDate: string,
  endDate: string,
  page: number,
  limit: number,
  searchParams?: SalesDetailSearchType,
) => {
  const params = {
    ...buildSalesDetailSearchParams(beginDate, endDate, searchParams),
    page,
    limit,
  }

  return http<Aipass.GetSalesManagerSalesResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales/search`,
    params,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * 	売上管理明細 一括精算
 */
export const adjustSales = (data: {
  saleIds: string[]
  salesPaymentId: string
  companyId: string | undefined
  salesAccountsReceivableMasterId: string | undefined
}) => {
  return http<Aipass.PutSalesManagerSalesAdjustmentResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/sales-manager/sales-adjustment`,
    data,
  })
}

/**
 * 	売上管理の入金設定の内容を取得
 */
export const fetchSalesPayment = () => {
  return http<Aipass.GetSalesManagerSalesPaymentResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales-payment`,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * 	売上管理の入金設定の編集
 */
export const updateSalesPayment = payment => {
  const data = { payment }
  return http<Aipass.PutSalesManagerSalesPaymentResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/sales-manager/sales-payment`,
    data,
  }).then(results => results.data)
}

/**
 * 	売上管理のレジ設定の内容を取得
 */
export const fetchSalesCashregister = () => {
  return http<Aipass.GetSalesManagerSalesCashRegisterResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales-cash-register`,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * 	売上管理のレジ設定の編集
 */
export const updateSalesCashregister = price => {
  const data = { price }
  return http<Aipass.PutSalesManagerSalesCashRegisterResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/sales-manager/sales-cash-register`,
    data,
  }).then(results => results.data)
}

/**
 * 	売上管理の現金売上取得
 */
export const fetchSalesDetailCash = (mode, salesManagerDailyId, salesManagerMonthId) => {
  const params = { salesManagerDailyId, salesManagerMonthId, mode }
  return http<Aipass.GetSalesManagerSalesCashResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales-cash`,
    params,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * 売上管理明細取得API
 */
export const fetchSales = checkinId => {
  const params = { checkinId }
  return http<Aipass.GetSalesManagerSalesResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales`,
    params,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * 売上管理明細取得API
 */
export const fetchSalesReservation = reservationId => {
  const params = { reservationId }
  return http<Aipass.GetSalesManagerSalesResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales`,
    params,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * 	売上管理の現金売上編集
 */
export const updateSalesDetailCash = (mode, salesManagerDailyId, salesManagerMonthId, cashData) => {
  const data = { ...{ mode, salesManagerDailyId, salesManagerMonthId }, ...cashData }
  return http<Aipass.PutSalesManagerSalesCashResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/sales-manager/sales-cash`,
    data,
  }).then(results => results.data)
}

/**
 * 	売上管理明細登録API
 */
export const updateSales = sales => {
  const data = { sales }
  return http<Aipass.PutSalesManagerSalesResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/sales-manager/admin-sales`,
    data,
  }).then(results => results.data)
}

/**
 * 締め処理後の売上修正
 */
export const postPostClosingSalesAdjustment = (data: PostClosingSalesAdjustmentParams) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/sales-manager/sales/post-closing-adjustment`,
    data,
  }).then(results => results.data)
}

/**
 * 	売上管理の確定
 */
export const confirmSalesDetail = (data: { salesManagerDailyId: string }) => {
  return http<{ nextSalesManagerId: string }>({
    method: 'put',
    url: `${env('BASE_API_V3')}/sales-manager/sales-confirm`,
    data,
  }).then(results => results.data)
}

/**
 * 	売上日報、月報の取得API
 */
export const fetchSalesReport = (mode, salesManagerDailyId, salesManagerMonthId) => {
  const params = { salesManagerDailyId, salesManagerMonthId, mode }
  return http<Aipass.GetSalesManagerSalesReportResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales-report`,
    params,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

export const fetchComExportRevCsv = (date_current_liability, start_date_period, end_date_period) => {
  const params = { date_current_liability, start_date_period, end_date_period }
  return http<{ url: string }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/com-export-rev-csv`,
    params,
    staffMutationTreatAsWrite: true,
  }).then(results => results.data)
}

export const fetchComExportCLCsv = (date_current_liability, start_date_period, end_date_period) => {
  const params = { date_current_liability, start_date_period, end_date_period }
  return http<{ url: string }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/com-export-cl-csv`,
    params,
    staffMutationTreatAsWrite: true,
  }).then(results => results.data)
}

/**
 * 	売上日報、月報の作成API
 */
export const updateSalesReport = (mode, salesManagerDailyId, salesManagerMonthId, letter) => {
  const data = { mode, salesManagerDailyId, salesManagerMonthId, letter }

  return http<Aipass.PostSalesManagerSalesReportResponse>({
    method: 'post',
    url: `${env('BASE_API_V3')}/sales-manager/sales-report`,
    data,
  }).then(results => results.data)
}

/**
 * 	カスタムチェックインの取得API
 */
type hoge = {
  (customCheckinId: string): Promise<CustomCheckinResponseType>
  (): Promise<CustomCheckinListResponseType>
}
export const fetchCustomCheckin: hoge = (customCheckinId?: string) => {
  const params = { customCheckinId }
  return http({
    method: 'get',
    url: `${env('BASE_API_V3')}/custom-checkin/admin-custom-checkin`,
    params,
  }).then(results => results.data)
}

/**
 * 	カスタムチェックインの作成API
 */
export const storeCustomCheckin = data => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/custom-checkin/admin-custom-checkin`,
    data,
  })
}

/**
 * 	カスタムチェックインの編集API
 */
export const updateCustomCheckin = (id: string, data) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/custom-checkin/admin-custom-checkin/${id}`,
    data,
  })
}

/**
 * 	カスタムチェックインの表示順入れ替えAPI
 */
export const updateCustomCheckinDisplayOrder = (sortedIds: string[]) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/custom-checkin/admin-custom-checkin/display-order`,
    data: { sortedIds },
  }).then(results => results.data)
}

/**
 * カスタムチェックインの削除API
 */
export const deleteCustomCheckin = customCheckinId => {
  const params = { customCheckinId }
  return http<Aipass.DeleteCustomCheckinAdminCustomCheckinResponse>({
    method: 'delete',
    url: `${env('BASE_API_V3')}/custom-checkin/admin-custom-checkin`,
    params,
  }).then(results => results.data)
}

/**
 * 	カスタムチェックインの公開状況更新API
 */
export const updateViewStatus = (data: { customCheckinId: string; viewStatus: number }) => {
  return http<Aipass.PutCustomCheckinAdminCustomCheckinStatusResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/custom-checkin/admin-custom-checkin-status`,
    data,
  }).then(results => results.data)
}

/**
 * アンケートの取得API
 */

export const fetchSurvey: SurveyFetchType = (surveyId?: string) => {
  if (surveyId) {
    // 設問詳細取得
    return http({
      method: 'get',
      url: `${env('BASE_API_V3')}/admin-surveys/questions/${surveyId}`,
    })
      .then(results => {
        // 新しいAPIのレスポンス構造に合わせて変換
        // 設問詳細取得APIでは、レスポンスが{question: {...}}の形式
        return results.data
      })
      .catch(e => {
        console.error(e)
        return { question: {} } as QuestionResponseType // エラー時は空のオブジェクトを返す
      })
  } else {
    // 設問一覧取得（hotelIdは認証から自動取得されるため不要）
    return http({
      method: 'get',
      url: `${env('BASE_API_V3')}/admin-surveys/questions`,
    })
      .then(results => {
        // 新しいAPIのレスポンス構造に合わせて変換
        const questions = results.data.questions || []
        return { surveys: questions.map(convertQuestionToSurvey) }
      })
      .catch(e => {
        console.error(e)
        return { surveys: [] } // エラー時は空の配列を返す
      })
  }
}

/**
 * アンケートの作成API（旧） - 非推奨
 * @deprecated 新しいAPI createSurvey を使用してください
 */
export const storeSurvey = data => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/survey/admin-survey`,
    data,
  })
}

/**
 * アンケートの作成API（新）
 */
export const createSurvey = (
  data: {
    title: string
    questionText: string
    type: 'score' | 'choice_single' | 'choice_multiple' | 'text'
    choices?: string[]
    isRequired: boolean
    isVisible: boolean
  },
  lang: string = 'ja',
) => {
  const apiLang = lang as 'ja' | 'en' | 'zh' | 'zhTw' | 'ko'

  const requestData = {
    lang: apiLang,
    title: data.title,
    questionText: data.questionText,
    type: data.type,
    choices: data.choices?.map(choice => ({ lang: apiLang, text: choice })),
    isRequired: data.isRequired,
    isVisible: data.isVisible,
  }

  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/admin-surveys/questions`,
    data: requestData,
  })
    .then(results => results.data)
    .catch(e => {
      console.error('新規作成API エラー:', e)
      throw e
    })
}

/**
 * アンケートの編集API（旧） - 非推奨
 * @deprecated 新しいAPI editSurvey を使用してください
 */
export const updateSurvey = (id: string, data) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/admin-surveys/questions/${id}`,
    data,
  })
}

/**
 * アンケートの編集API（新）
 */
export const editSurvey = (
  questionId: string,
  data: {
    title: string
    questionText: string
    type?: 'score' | 'choice_single' | 'choice_multiple' | 'text'
    choices?: string[]
    isRequired: boolean
    isVisible: boolean
  },
  lang: string = 'ja',
) => {
  const apiLang = lang as 'ja' | 'en' | 'zh' | 'zhTw' | 'ko'

  const requestData = {
    lang: apiLang,
    title: data.title,
    questionText: data.questionText,
    type: data.type,
    choices: data.choices?.map(choice => ({ lang: apiLang, text: choice })),
    isRequired: data.isRequired,
    isVisible: data.isVisible,
  }

  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/admin-surveys/questions/${questionId}`,
    data: requestData,
  })
    .then(results => results.data)
    .catch(e => {
      console.error('編集API エラー:', e)
      throw e
    })
}

/**
 * アンケートの表示順入れ替えAPI（旧）
 */
export const updateSurveyDisplayOrder = (sortedIds: string[]) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/survey/admin-survey/display-order`,
    data: { sortedIds },
  }).then(results => results.data)
}

/**
 * アンケートの表示順入れ替えAPI（新）
 */
export const updateSurveyOrder = (order: Array<{ questionId: string; sortOrder: number }>) => {
  const requestData = { order }
  console.log('並び替えAPI送信データ:', requestData)

  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/admin-surveys/questions/order`,
    data: requestData,
  })
    .then(results => results.data)
    .catch(e => {
      console.error('並び替えAPI エラー:', e)
      throw e
    })
}

/**
 * アンケートの削除API（単一削除）
 */
export const deleteSurvey = (questionId: string) => {
  console.log('単一削除API送信データ:', { questionId })

  return http({
    method: 'delete',
    url: `${env('BASE_API_V3')}/admin-surveys/questions/${questionId}`,
  })
    .then(results => results.data)
    .catch(e => {
      console.error('単一削除API エラー:', e)
      throw e
    })
}

/**
 * アンケートの削除API（単一・複数対応）
 */
export const deleteSurveys = (questionIds: string[]) => {
  console.log('削除API送信データ:', { questionIds })

  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/admin-surveys/questions/delete`,
    data: { questionIds },
  })
    .then(results => results.data)
    .catch(e => {
      console.error('削除API エラー:', e)
      throw e
    })
}

/**
 * アンケートの公開状況更新API（旧）
 */
export const updateSurveyViewStatus = (data: { surveyId: string; viewStatus: number }) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/survey/admin-survey-status`,
    data,
  }).then(results => results.data)
}

/**
 * アンケートの表示・非表示トグルAPI（新）
 */
export const updateSurveyVisibility = (questionId: string, isVisible: boolean) => {
  console.log('表示非表示更新API送信データ:', { questionId, isVisible })

  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/admin-surveys/questions/${questionId}/visibility`,
    data: { isVisible },
  })
    .then(results => results.data)
    .catch(e => {
      console.error('表示非表示更新API エラー:', e)
      throw e
    })
}

/**
 * GoogleMap設定取得API
 */
export const fetchGoogleMapSettings = () => {
  console.log('GoogleMap設定取得API送信データ: {}')

  return http({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-surveys/settings/google-map`,
  })
    .then(results => {
      // hotelIdを除外して必要なデータのみ返す
      const { hotelId, ...settings } = results.data
      return settings
    })
    .catch(e => {
      console.error('GoogleMap設定取得API エラー:', e)
      throw e
    })
}

/**
 * GoogleMap設定更新API
 */
export const updateGoogleMapSettings = (data: { googleMapEnabled: boolean; googleMapUrl: string | null }) => {
  console.log('GoogleMap設定更新API送信データ:', data)

  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/admin-surveys/settings/google-map`,
    data,
  })
    .then(results => results.data)
    .catch(e => {
      console.error('GoogleMap設定更新API エラー:', e)
      throw e
    })
}

export const fetchTimeSelection = () => {
  return http<{ timeSelections: TimeSelectionIndex[] }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/custom-checkin/time-selection`,
  })
    .then(result => result.data.timeSelections)
    .catch(e => console.error(e))
}

export const fetchTimeSelectionDetail = (id: string) => {
  return http<any>({
    method: 'get',
    url: `${env('BASE_API_V3')}/custom-checkin/time-selection/${id}`,
  }).then(result => result.data)
}

export const createTimeSelection = (data: TimeSelectionSubmitData) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/custom-checkin/time-selection`,
    data,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

export const updateTimeSelection = (data: TimeSelectionSubmitData & { id: string }) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/custom-checkin/time-selection/${data.id}`,
    data,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

export const updateTimeSelectionViewState = (id: string, isVisible: boolean) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/custom-checkin/time-selection/view-state`,
    data: { id, isVisible },
  })
}

export const deleteTimeSelection = (ids: string[]) => {
  return http({
    method: 'delete',
    url: `${env('BASE_API_V3')}/custom-checkin/time-selection/bulk-delete`,
    params: { ids },
  })
}

export const updateTimeSelectionDisplayOrder = (sortedIds: string[]) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/custom-checkin/time-selection/display-order`,
    data: { sortedIds },
  })
}

/**
 * 	利用明細領収書CSVデータの取得API
 */
export const fetchCheckinSalesDetailCsv = checkinId => {
  const params = { checkinId }
  return http<Aipass.GetSalesManagerSalesCsvResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales-csv`,
    params,
    staffMutationTreatAsWrite: true,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * 	利用明細領収書PDFデータの取得API
 */
export const fetchCheckinSalesDetailPdf = (params: SalesDetailPdfParams) => {
  return http<{ url: string }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales-pdf`,
    params,
    paramsSerializer: params => {
      return qs.stringify(params)
    },
    staffMutationTreatAsWrite: true,
  }).then(results => results.data)
}

export const issueCheckinSalesSplitDetailPdf = (params: SalesSplitDetailPdfParams) => {
  return http<{ url: string }>({
    method: 'post',
    url: `${env('BASE_API_V3')}/sales-manager/sales-split-pdf`,
    data: params,
    staffMutationTreatAsWrite: true,
  }).then(results => results.data)
}

/*
 * 	宿泊管理CSVデータの取得API
 */
/**
 * 宿泊管理 CSV 作成依頼。呼び出し元の try/catch やスタッフガードのため、失敗時は必ず reject する（.catch で握りつぶさないこと）。
 */
export const fetchAccommodationManagementCsv = async (data: any) => {
  const results = await http<Aipass.GetAdminAccommodationManagementCsvResponse>({
    method: 'post',
    url: `${env('BASE_API_V3')}/accommodation-management-download-csv-sqs`,
    data,
  })
  return results.data
}

export const downloadCustomerListCsv = data => {
  return http<Omit<SearchCustomerListParams, 'page' | 'limit'>>({
    method: 'post',
    url: `${env('BASE_API_V3')}/customer-list-download-csv-sqs`,
    data,
  }).then(results => results.data)
}

export const fetchCustomerListCsvHistory = () => {
  const params = {}
  return http({
    method: 'get',
    url: `${env('BASE_API_V3')}/customer-list-download-csv-history`,
    params,
  }).then(results => results.data.downloadCsvHistories)
}

/*
 * 	CSVアップロード履歴の取得API
 */
export const fetchUploadCsvHistory = () => {
  const params = {}
  return http<CsvUploadHistoryResponseType[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-csv-upload-history`,
    params,
  }).then(results => results.data)
}

/*
 * 	宿泊管理CSVダウンロード履歴の取得API
 */
export const fetchDownloadCsvHistory = () => {
  const params = {}
  return http<Aipass.GetAccommodationManagementDownloadCsvHistoryResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/accommodation-management-download-csv-history`,
    params,
  })
    .then(results => results.data.DownloadCsvHistorys)
    .catch(e => console.error(e))
}

export const fetchReceiptHistory = (params: {
  page: number
  customerId?: string | null
  checkinId?: string | null
  reservationId?: string | null
  fileName?: string
  searchBeginDate?: number
  searchEndDate?: number
}) => {
  return http<{
    histories: Array<{ id: number; fileName: string; isEnabled: boolean; createdAt: string; url: string | undefined }>
    total: number
  }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales-pdf-history`,
    params,
  }).then(results => results.data)
}

export const updateReceiptHistory = (uploadReceiptLogsId: string, isEnabled: boolean) => {
  const data = { uploadReceiptLogsId, isEnabled }
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/sales-manager/sales-pdf-history`,
    data,
  })
}

/**
 * 売上管理CSVデータの取得API
 */
export const fetchSalesDetailCsv = (beginDate: string = '', endDate: string = '', searchParams?: SalesDetailSearchType) => {
  const params = buildSalesDetailSearchParams(beginDate, endDate, searchParams)

  return http<Aipass.GetSalesManagerSalesDetailListCsvResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales-detail-list-csv`,
    params,
    staffMutationTreatAsWrite: true,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * 入金一覧CSVデータの取得API
 */
export const fetchSalePaymentListCsv = (beginDate: string | undefined, endDate: string | undefined, salesManagerMonthId?) => {
  const params = {
    beginDate: dayjs(beginDate).unix(),
    endDate: dayjs(endDate).unix(),
    salesManagerMonthId,
  }
  return http<Aipass.GetSalesManagerPaymentListCsvResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales-payment-list-csv`,
    params,
    staffMutationTreatAsWrite: true,
  })
    .then((results: AxiosResponse<Aipass.GetSalesManagerPaymentListCsvResponse>) => {
      return results.data
    })
    .catch(e => console.error(e))
}

/**
 * カスタムチェックインの設定内容 取得API
 */
export const fetchGuide = hotelId => {
  const params = {
    hotelId,
  }
  return http<Aipass.GetCustomCheckinCustomCheckinResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/custom-checkin/custom-checkin`,
    params,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

export const fetchTimeSelectionSpace = (hotelId: string, reservationIds: string[]) => {
  const params = { hotelId, reservationIds }
  return http<Aipass.GetCustomCheckinCustomCheckinResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/custom-checkin/custom-checkin-time-selection`,
    params,
    paramsSerializer: params => qs.stringify(params),
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * チェックイン詳細のゲスト情報追加API
 */
export const updateGuestInfo = (data: UpdateGuestParams) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/admin/checkin-guest`,
    data,
  })
}

/**
 * ゲストチェックインのゲスト情報追加API
 */
export const updateSelfCheckin = (checkin: object, reservationId: string) => {
  const data = {
    checkin,
    reservationIds: [reservationId],
  }

  return http<{ checkinId: string; checkinCode: string; qrJson: string }>({
    method: 'post',
    url: `${env('BASE_API_V3')}/hotel/admin-checkin-approved`,
    data,
  }).then(results => results.data)
}

/*
 * 	同じグループの代表者、同行者に1人でもチェックインリストにいるかどうか
 */
export const fetchIsInSameGroupMemberCheckin = checkinId => {
  const params = { checkinId }
  return http<Aipass.GetAdminGroupMemberResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-checkin-group-member`,

    params,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/*
 * 	売上管理の割引設定の内容を取得
 */
export const fetchSalesDiscount = () => {
  return http<{ salesDiscount: Required<DiscountSettingType>[] }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales-discount`,
  })
    .then(results => results.data)
    .catch(e => {
      console.error(e)
      return { salesDiscount: [] }
    })
}

/**
 * 	売上管理の割引設定の編集
 */
export const updateSalesDiscount = (discount: DiscountSettingType[]) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/sales-manager/sales-discount`,
    data: { discount },
  })
}

/*
 * 	チェックイン可能な予約を取得するAPI
 */
export const fetchAdminReservationForCheckin = (beginDate, endDate, searchText, page, limit = 30, reservationIds) => {
  const params = {
    beginDate: beginDate ? dayjs(beginDate).format('YYYY-MM-DD') : undefined,
    endDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : undefined,
    searchText,
    page,
    limit,
    reservationIds,
  }

  return http<Aipass.GetAdminReservationForCheckinResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-reservation-for-checkin`,

    params,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * 売上管理割引取得API
 */
export const fetchDiscount = checkinId => {
  const params = { checkinId, type: 'discount' }
  return http<Aipass.GetSalesManagerSalesResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales`,
    params,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * 売上管理割引取得API
 */
export const fetchReservationDiscount = reservationId => {
  const params = { reservationId, type: 'discount' }
  return http<Aipass.GetSalesManagerSalesResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales`,
    params,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * 予約者の確定済み明細取得API
 */
export const fetchReservationConfirmedSales = reservationId => {
  const params = { reservationId, type: 'confirmedSales' }
  return http<Aipass.GetSalesManagerSalesResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales`,
    params,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * アイコン表示非表示 取得API
 */
export const fetchIconDisplay = hotelId => {
  const params = { hotelId }
  return http<Aipass.GetIconDisplayResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/icon-display`,
    params,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * アイコン表示非表示 更新API
 */
export const updateIconStatus = iconDisplay => {
  const data = { iconDisplay }
  return http<Aipass.PutIconDisplayResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/admin-icon-display`,
    data,
  }).then(results => results.data)
}

/**
 * 全体掲示板 取得API
 */
export const fetchDashboardNote = (date: string) => {
  return http<{
    commonly: { note: string }
    daily: { note: string }
  }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/dashboard-note`,
    params: { date },
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
  全体掲示板 更新API
 */
export const updateDashboardNote = (data: { note: string; date: string | undefined }) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/dashboard-note`,
    data: {
      ...data,
      hogehoge: undefined,
      hufafuga: null,
      bar: '',
    },
  })
}

/**
 * セルフチェックインでの館内案内情報 基本情報取得API
 */
export const fetchFacilityBasicInfoInSelfCheckin = (hotelId: string) => {
  const params = { hotelId: hotelId }
  return http<GetSelfCheckinSelfCheckinBasicInfoResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/self-checkin/self-checkin-basic-info`,
    params,
  })
    .then(results => results.data)
    .catch(e => {
      console.error(e)
      throw e
    })
}

export const fetchMobileRequestProducts = () => {
  return http<MobileRequest.ProductType[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/mobile-request/admin-products`,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}
export const postMobileRequestProducts = (data: MobileRequest.ProductInputType) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/mobile-request/admin-products`,
    data,
  })
}
export const putMobileRequestProducts = (id: string, data: MobileRequest.ProductInputType) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/mobile-request/admin-products/${id}`,
    data,
  })
}
export const deleteMobileRequestProducts = (ids: string[]) => {
  return http({
    method: 'delete',
    url: `${env('BASE_API_V3')}/mobile-request/admin-products`,
    params: { ids },
  })
}

export const fetchMobileRequestCategories = () => {
  return http<MobileRequest.CategoryType[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/mobile-request/admin-categories`,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}
export const postMobileRequestCategories = (data: MobileRequest.CategoryInputType) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/mobile-request/admin-categories`,
    data,
  })
}
export const putMobileRequestCategories = (id: number, data: MobileRequest.CategoryInputType) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/mobile-request/admin-categories/${id}`,
    data,
  })
}
export const deleteMobileRequestCategories = (ids: number[]) => {
  return http({
    method: 'delete',
    url: `${env('BASE_API_V3')}/mobile-request/admin-categories`,
    params: { ids },
  })
}

export const fetchMobileRequestTags = () => {
  return http<MobileRequest.TagType[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/mobile-request/admin-tags`,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}
export const postMobileRequestTags = (data: MobileRequest.TagInputType) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/mobile-request/admin-tags`,
    data,
  })
}
export const putMobileRequestTags = (id: number, data: MobileRequest.TagInputType) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/mobile-request/admin-tags/${id}`,
    data,
  })
}
export const deleteMobileRequestTags = (ids: number[]) => {
  return http({
    method: 'delete',
    url: `${env('BASE_API_V3')}/mobile-request/admin-tags`,
    params: { ids },
  })
}

export const fetchMobileRequestSalesHours = () => {
  return http<MobileRequest.SalesHourType[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/mobile-request/admin-sales-hours`,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}
export const postMobileRequestSalesHours = (data: MobileRequest.SalesHourInputType) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/mobile-request/admin-sales-hours`,
    data,
  })
}
export const putMobileRequestSalesHours = (id: number, data: MobileRequest.SalesHourInputType) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/mobile-request/admin-sales-hours/${id}`,
    data,
  })
}
export const deleteMobileRequestSalesHours = (ids: number[]) => {
  return http({
    method: 'delete',
    url: `${env('BASE_API_V3')}/mobile-request/admin-sales-hours`,
    params: { ids },
  })
}

export const fetchSalesDepartmentForRequest = () => {
  return http<{
    departments: Array<MobileRequest.SalesDepartmentType>
  }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/mobile-request/admin-sales-departments`,
  })
    .then(results => results.data.departments)
    .catch(e => console.error(e))
}

export const fetchSalesSubjectForProduct = departmentId => {
  const params = { departmentId }
  return http<{ subjects: MobileRequest.SalesSubjectType[] }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/mobile-request/admin-sales-subjects`,
    params,
  })
    .then(results => results.data.subjects)
    .catch(e => console.error(e))
}

export const updateMobileRequestEmailOrder = (params: MobileRequest.NotificationInputType) => {
  return http<Aipass.PutSettingEmailOrder>({
    method: 'put',
    url: `${env('BASE_API_V3')}/mobile-request/admin-email`,
    params,
  })
}

/**
 * モバイルリクエスト リクエスト一覧取得
 */
export const fetchOrders = (beginDate, endDate, page, limit) => {
  const params = {
    beginDate: beginDate === '' ? 0 : dayjs(beginDate).unix(),
    endDate: endDate === '' ? 0 : dayjs(endDate).unix(),
    page,
    limit,
  }
  return http({
    method: 'get',
    url: `${env('BASE_API_V3')}/mobile-request/admin-order`,
    params,
  }).then(results => results.data)
}

/**
 * モバイルリクエスト リクエスト一覧詳細取得
 */
export const fetchUserOrders = checkinId => {
  const params = { checkinId }
  return http({
    method: 'get',
    url: `${env('BASE_API_V3')}/mobile-request/admin-order-detail`,
    params,
  }).then(results => results.data)
}

/**
 * モバイルリクエスト ステータス更新
 */
export const updateOrderStatus = (orderId: string | string[], status, checkinId?) => {
  const data = {
    orderId: typeof orderId === 'string' ? orderId : null,
    orderIds: typeof orderId !== 'string' ? orderId : null,
    status,
    checkinId,
  }
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/mobile-request/admin-order-detail`,
    data,
  }).then(results => results.data)
}

/**
 * 予約新規作成
 */
export const createReservation = (props: {
  checkinDate: string
  checkoutDate: string
  reservationName?: string
  skipUserDataUpdate?: boolean
}) => {
  const data = {
    checkinDate: dayjs(props.checkinDate).add(12, 'h').format('YYYY-MM-DD'),
    checkoutDate: dayjs(props.checkoutDate).add(12, 'h').format('YYYY-MM-DD'),
    reservationName: props.reservationName,
    skipUserDataUpdate: props.skipUserDataUpdate,
  }
  return http<Aipass.PostAdminReservationResponse>({
    method: 'post',
    url: `${env('BASE_API_V3')}/admin-reservation`,
    data,
  }).then(results => results.data)
}

/**
 * 予約複製前の在庫チェック
 */
export const validateReservationStock = (props: { reservationId: string; checkinDate: string; checkoutDate: string }) => {
  const data = {
    reservationId: props.reservationId,
    checkinDate: dayjs(props.checkinDate).format('YYYY-MM-DD'),
    checkoutDate: dayjs(props.checkoutDate).format('YYYY-MM-DD'),
  }
  return http<{
    statusCode: number
  }>({
    method: 'post',
    url: `${env('BASE_API_V3')}/admin-reservation/validate-stock`,
    data,
  }).then(results => results.data)
}

/**
 * 予約複製
 */
export const copyReservation = (props: { reservationId: string; checkinDate: string; checkoutDate: string }) => {
  const data = {
    reservationId: props.reservationId,
    checkinDate: dayjs(props.checkinDate).format('YYYY-MM-DD'),
    checkoutDate: dayjs(props.checkoutDate).format('YYYY-MM-DD'),
  }
  return http<{
    statusCode: number
    reservationId: string // UUID形式の予約ID（画面遷移に使用）
    inventoryWarnings?: Array<{
      room_type: string
      date: string
      remaining_count: number
    }>
    errorMessage?: string
    errors?: Record<string, string[]>
  }>({
    method: 'post',
    url: `${env('BASE_API_V3')}/admin-reservation/copy`,
    data,
  }).then(results => results.data)
}

/**
 * 帳票 取得API
 */
export const fetchBusinessForms = (beginDate, endDate, numberOfRooms) => {
  const params = {
    beginDate: dayjs(beginDate).format('YYYY-MM-DD'),
    endDate: dayjs(endDate).format('YYYY-MM-DD'),
    numberOfRooms,
  }
  return http<Aipass.GetSalesManagerBusinessFormsResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales-business-forms`,
    params,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * 帳票日別CSV 取得API
 */
export const fetchBusinessFormsCsv = (beginDate = '', endDate = '', numberOfRooms = '', mode = 'daily') => {
  const params = {
    beginDate: dayjs(beginDate).format('YYYY-MM-DD'),
    endDate: dayjs(endDate).format('YYYY-MM-DD'),
    numberOfRooms,
    mode,
  }
  return http<Aipass.GetSalesManagerBusinessFormsCsvResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales-business-forms-csv`,
    params,
    staffMutationTreatAsWrite: true,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * RemoteLock
 * クライアント情報を取得
 */
export const getClientInfo = () => {
  return http<Aipass.GetRemoteLockClientInfoResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/remote-lock/client-info`,
  }).then(results => results.data)
}

/**
 * RemoteLock
 * 連携方法の更新（連携フラグの更新）
 */
export const postClientCoordination = (approvalCode: String) => {
  const data = { approvalCode }
  return http<Aipass.PostRemoteLockClientCoordinationResponse>({
    method: 'post',
    url: `${env('BASE_API_V3')}/remote-lock/client-coordination/token`,

    data,
  }).then(results => results.data)
}

/**
 * RemoteLock
 * ゲストルームのデバイスを最新に更新する
 */
export const remoteLockSyncDevices = () => {
  return http<Aipass.PutRemoteLockRemoteLockDeviceResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/remote-lock/devices/sync`,
  }).then(results => results.data)
}

/**
 * RemoteLock
 * アカウントとクライアントとの連携を解除する
 */
export const cancelClientCoordination = () => {
  return http<Aipass.PutRemoteLockClientCoordinationResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/remote-lock/client-coordination/revoke`,
  }).then(results => results.data)
}

/**
 * 機器の設定情報取得API
 */
export const fetchDeviceSetting = (hotelId?: string) => {
  const url = hotelId ? `${env('BASE_API_V3')}/self-checkin/device-setting?hotel_id=${hotelId}` : `${env('BASE_API_V3')}/device-setting`
  return http<deviceSettingResponseType>({
    method: 'get',
    url,
  })
    .then(results => results.data.deviceSettings)
    .catch(e => console.error(e))
}

/**
 * セルフチェックイン設定情報取得API
 */
export const fetchSelfCheckinSetting = hotelId => {
  const params = { hotelId }
  return http<SelfCheckinSettingResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/self-checkin/self-checkin-setting`,
    params,
  })
    .then(results => results.data)
    .catch(e => {
      console.error(e)
      return undefined
    })
}

/**
 * セルフチェックイン設定情報更新API
 */
export const putSelfCheckinSetting = param => {
  const data = param
  return http<Aipass.PutSelfCheckinSettingResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/self-checkin/self-checkin-setting`,
    data,
  }).then(results => results.data)
}

export const putSelfCheckinSearchSettingIsEnabled = (data: SearchSetting) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/self-checkin/self-checkin-search-setting/enabled`,
    data,
  }).then(results => results.data)
}

export const putSelfCheckinSearchSettingSortOrder = (data: { searchItems: SearchSetting[] }) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/self-checkin/self-checkin-search-setting/order`,
    data,
  }).then(results => results.data)
}

/**
 * 顧客一覧取得API
 */
export const fetchCustomers = (searchParams: SearchCustomerListParams) => {
  return http<Aipass.FetchCustomersResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/customer-list`,
    params: searchParams,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * 客室番号設定
 * 客室番号の一覧を取得
 */
export const getAdminGuestRoomNumber = () => {
  return http<GuestRoomNumberType[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-guest-room-number`,
  })
    .then(results => results.data)
    .catch(e => {
      console.error(e)
      return []
    })
}

/**
 * 顧客情報編集API
 */
export const updateCustomerInfo = customer => {
  const data = {
    checkin: {
      name: customer.name,
      nameKana: customer.nameKana,
      birthDate: customer.birthDate,
      nationality: customer.nationality,
      gender: customer.gender,
      postalCode: customer.postalCode,
      address: customer.address,
      telephone: customer.telephone,
      email: customer.email,
      occupation: customer.occupation,
      otherOccupation: customer.otherOccupation,
      picture: customer.picture,
      company: customer.company,
      hotelCuicinUserId: customer.hotelCuicinUserId,
      deliveryAgreementStatus: customer.deliveryAgreementStatus,
    },
  }

  return http<Aipass.PutCustomerInfoResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/customer-info`,
    data,
  }).then(results => results.data)
}

export const deleteCustomerInfo = (id: string) => {
  return http({
    method: 'delete',
    url: `${env('BASE_API_V3')}/customer-info/${id}`,
  })
}

export const updateCustomerTag = (customer, tag) => {
  const data = {
    tag: tag || '',
  }

  return http<Aipass.PutCustomerInfoResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/customer-tag/${customer.hotelCuicinUserId}`,
    data,
  }).then(results => results.data)
}

/**
 * 顧客検索取得API
 */
export const fetchCustomerSearch = (params: {
  name?: string
  nameKana?: string
  guestTelephone?: string
  userTelephone?: string
  page: number
  limit: number
}) => {
  return http<Aipass.FetchCustomersResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/customer-search`,
    params,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * 客室番号設定
 * 客室番号の追加
 */
export const insertAdminGuestRoomNumber = (data: {
  displayOrder: string
  assignOrder: string
  roomNumber: string
  roomTypeId: string
  roomFloorId: string
  capacity: string | undefined
}) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/settings/guest-room`,
    data,
  })
}

/**
 * 客室番号設定
 * 客室番号の更新&削除
 */
export const putAdminGuestRoomNumber = (
  id: string,
  data: {
    displayOrder: string
    assignOrder: string
    roomNumber: string
    roomTypeId: string
    roomFloorId: string
    capacity: string | undefined
  },
) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/settings/guest-room/${id}`,
    data,
  })
}

export const deleteAdminGuestRoomNumber = (ids: string[]) => {
  return http({
    method: 'delete',
    url: `${env('BASE_API_V3')}/settings/guest-room`,
    params: { ids },
  })
}

/**
 * 客室番号設定
 * 客室番号CSVでの追加
 */
export const insertAdminGuestRoomNumberCsv = csvRows => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/admin-guest-room-number-csv`,
    data: { csvRows },
  })
}

/**
 * 客室
 * 客室タイプのセレクト一覧取得
 */
export const getAdminGuestRoomTypeSelect = () => {
  return http<GuestRoomTypeSelectType[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-guest-room-type-select`,
  })
    .then(results => results.data)
    .catch(e => {
      console.error(e)
      return []
    })
}

/**
 * 顧客紐付けAPI
 */
export const createCustomerLink = (checkinId, reservationId, hotelCuicinUserId) => {
  return http<Aipass.PostCustomerLinkResponse>({
    method: 'post',
    url: `${env('BASE_API_V3')}/customer-link`,
    data: { checkinId, reservationId, hotelCuicinUserId },
  })
}

/**
 * 客室タイプ設定
 * 客室タイプの一覧を取得
 */
export const getAdminGuestRoomType = () => {
  return http<Aipass.GetAdminGuestRoomTypeResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-guest-room-type`,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * 客室タイプ設定
 * 客室タイプの追加
 */
export const insertAdminGuestRoomType = (data: GuestRoomTypeInputType) => {
  return http<Aipass.PostAdminGuestRoomTypeResponse>({
    method: 'post',
    url: `${env('BASE_API_V3')}/admin-guest-room-type`,

    data,
  }).then(results => results.data)
}

/**
 * 客室タイプ設定
 * 客室タイプの更新
 */
export const putAdminGuestRoomType = (id: string, data: GuestRoomTypeInputType) => {
  return http<Aipass.PutAdminGuestRoomTypeResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/admin-guest-room-type/${id}`,
    data,
  }).then(results => results.data)
}

/**
 * 客室タイプ設定
 * 客室タイプの削除
 */
export const deleteAdminGuestRoomType = (id: string[]) => {
  return http<Aipass.PutAdminGuestRoomTypeResponse>({
    method: 'delete',
    url: `${env('BASE_API_V3')}/admin-guest-room-type`,
    params: { id },
  }).then(results => results.data)
}

/**
 * 顧客紐付け解除API
 */
export const deleteCustomerLink = (checkinId, reservationId) => {
  return http<Aipass.PostAdminCheckinIdentificationResponse>({
    method: 'delete',
    url: `${env('BASE_API_V3')}/customer-link`,
    params: { checkinId, reservationId },
  })
}

/**
 * 顧客履歴一覧取得API
 */
export const fetchCustomerHistory = hotelCuicinUserId => {
  const params = { hotelCuicinUserId }
  return http<Aipass.FetchCustomerHistoryResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/customer-history`,
    params,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * 客室フロア設定
 * 客室フロアの一覧取得
 */
export const getAdminGuestRoomFloor = () => {
  return http<GuestRoomFloorType[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-guest-room-floor`,
  })
    .then(results => results.data)
    .catch(e => {
      console.error(e)
      return []
    })
}

/**
 * 客室フロア設定
 * 客室フロアの追加
 */
export const insertAdminGuestRoomFloor = (data: { displayOrder: string | undefined; roomFloorName: string }) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/admin-guest-room-floor`,
    data,
  })
}

/**
 * 客室フロア設定
 * 客室フロアの更新&削除
 */
export const putAdminGuestRoomFloor = (id: string, data: { displayOrder: string | undefined; roomFloorName: string }) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/admin-guest-room-floor/${id}`,
    data,
  })
}

export const deleteAdminGuestRoomFloor = (params: { ids: string[] }) => {
  return http({
    method: 'delete',
    url: `${env('BASE_API_V3')}/admin-guest-room-floor`,
    params,
  })
}

/**
 * チェックイン受付 更新API
 */
export const updateCheckinReception = (checkinIds, approvedStatus: number, mode) => {
  const data = { checkinIds, approvedStatus, mode }

  return http<Aipass.PutCheckinReceptionResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/admin-checkin-reception`,
    data,
  })
}

/**
 * 清掃ステータス付け客室一覧 取得API
 */
export const fetchStaffByMonth = selectedDate => {
  return http<CLeaningManager.StaffType[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/cleaning-manager/staff-assign`,
    params: { dateOf: selectedDate },
  }).then(results => results.data)
}
export const fetchMonthSummary = selectedDate => {
  return http({
    method: 'get',
    url: `${env('BASE_API_V3')}/cleaning-manager/month-summary`,
    params: { dateOf: selectedDate },
  }).then(results => results.data)
}
export const fetchListRoom = selectedDate => {
  return http<Aipass.FetchGuestRoomForCleaningManager>({
    method: 'get',
    url: `${env('BASE_API_V3')}/cleaning-manager/guest-rooms`,
    params: { dateOf: selectedDate },
  }).then(results => results.data)
}
export const exportListRoom = (format: 'pdf' | 'csv', selectedDate: string) => {
  return http<{ url: string }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/cleaning-manager/guest-rooms/report/${format}`,
    params: { dateOf: selectedDate },
  }).then(results => results.data?.url)
}

/**
 * 清掃ステータス付け客室詳細 取得API
 */
export const fetchRoomDetail = (id, selectedDate) => {
  return http<Aipass.FetchGuestRoomDetailForCleaningManager>({
    method: 'get',
    url: `${env('BASE_API_V3')}/cleaning-manager/guest-room-detail`,
    params: { dateOf: selectedDate, guestRoomId: id },
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

export const assignCleaningStaff = (data: { guestRoomIds: string[]; dateOf: string; staffId: string }) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/cleaning-manager/assign`,
    data,
  })
}

export const toggleCleaningStaffShift = (data: { dateOf: string; staffId: string }) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/cleaning-manager/toggleShift`,
    data,
  })
}
export const fetchCleaningStaff = () => {
  return http<CLeaningManager.StaffSettingType[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/cleaning-manager/staff`,
  })
    .then(result => result.data)
    .catch(e => console.error(e))
}
export const createCleaningStaff = (data: CLeaningManager.staffFormType) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/cleaning-manager/staff`,
    data,
  })
}
export const updateCleaningStaff = (data: CLeaningManager.staffFormType, id: string) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/cleaning-manager/staff/${id}`,
    data,
  })
}
export const deleteCleaningStaff = (ids: string[]) => {
  return http({
    method: 'delete',
    url: `${env('BASE_API_V3')}/cleaning-manager/staff`,
    params: { ids },
  })
}
export const fetchCleaningManagerSetting = () => {
  return http<CLeaningManager.CheckListType[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/cleaning-manager/check-list`,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}
export const postCleaningManagerSetting = (data: CLeaningManager.CheckListFormType) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/cleaning-manager/check-list`,
    data,
  })
}
export const putCleaningManagerSetting = (id: number, data: CLeaningManager.CheckListFormType) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/cleaning-manager/check-list/${id}`,
    data,
  })
}
export const deleteCleaningManagerSetting = (id: number[]) => {
  return http({
    method: 'delete',
    url: `${env('BASE_API_V3')}/cleaning-manager/check-list`,
    params: { id },
  })
}
export const putCleaningRoomCheckList = (data: { guestRoomId: string; dateOf: string; checkListId: number; isChecked: boolean }) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/cleaning-manager/guest-room/check-item`,
    data,
  })
}

/**
 * 清掃ステータス 更新API
 */
export type UpdateGuestRoomStatusForCleaningManagerRequestType = {
  guestRoomId: string
  dateOf: Dayjs
  staffReport: string | undefined
  instruction?: string
  cleaningStatus: CleaningStatusType | undefined
  cleaningStaffId: string | undefined
  roomUsageStatus: RoomUsageStatusType | undefined
  images?: Array<{ path: string; uploadThumbnail?: string }>
}
export const updateGuestRoomStatusForCleaningManager = (input: UpdateGuestRoomStatusForCleaningManagerRequestType) => {
  return http<Aipass.UpdateGuestRoomDetailStatusForCleaningManager>({
    method: 'put',
    url: `${env('BASE_API_V3')}/cleaning-manager/guest-room-status`,
    data: {
      ...input,
      dateOf: input.dateOf.format('YYYY-MM-DD'),
      // フロント側プレビュー用 uploadThumbnail (blob URL等) を除外し、path のみを送信
      images: input.images?.map(({ path }) => ({ path })),
    },
  }).then(results => results.data)
}

/**
 * 全体共有内容 更新API
 */
export const updateAnnounceForCleaningManager = (dateOf: Dayjs, announce: string) => {
  return http<Aipass.UpdateAnnounceForCleaningManager>({
    method: 'post',
    url: `${env('BASE_API_V3')}/cleaning-manager/announce`,
    data: { dateOf: dateOf.format('YYYY-MM-DD'), announce },
  }).then(results => results.data)
}

export const bulkUpdateGuestRoomStatusForCleaningManager = (data: { from: CleaningStatusType; to: CleaningStatusType; dateOf: Dayjs }) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/cleaning-manager/bulk-guest-room-status`,
    data: {
      ...data,
      dateOf: data.dateOf.format('YYYY-MM-DD'),
    },
  })
}

/**
 * 売掛先 取得API
 */
export const fetchSalesAccountsReceivable = () => {
  return http<Aipass.FetchSalesManagerSalesAccountsReceivableResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales-accounts-receivable`,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * 売掛先 更新API
 */
export const updateSalesAccountsReceivable = ({ accountsReceivables, accountsReceivablesToDelete }) => {
  return http<Aipass.PutSalesManagerSalesAccountsReceivableResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/sales-manager/sales-accounts-receivable-master`,
    data: { accountsReceivables, accountsReceivablesToDelete },
  }).then(results => results.data)
}

export const fetchSalesDiscountARRules = () => {
  return http<DiscountARRule[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales-discount-ar-rules`,
  })
    .then(results => results.data)
    .catch(e => {
      console.error(e)
      return []
    })
}

export const storeSalesDiscountARRules = (data: DiscountARRuleInputParams) => {
  return http<Aipass.PutSalesManagerSalesAccountsReceivableResponse>({
    method: 'post',
    url: `${env('BASE_API_V3')}/sales-manager/sales-discount-ar-rules`,
    data,
  })
}

export const updateSalesDiscountARRules = (id: string, data: DiscountARRuleInputParams) => {
  return http<Aipass.PutSalesManagerSalesAccountsReceivableResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/sales-manager/sales-discount-ar-rules/${id}`,
    data,
  })
}

export const deleteSalesDiscountARRules = (ids: string[]) => {
  return http<Aipass.PutSalesManagerSalesAccountsReceivableResponse>({
    method: 'delete',
    url: `${env('BASE_API_V3')}/sales-manager/sales-discount-ar-rules/bulk-delete`,
    params: { ids },
  })
}

/**
 * 予約連携設定
 * マーケットセグメント取得
 */
export const fetchReservationMarketSegment = () => {
  return http<MarketSegmentModel.MarketSegmentType[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/reservation-market-segment`,
  })
    .then(results => results.data)
    .catch(e => {
      console.error(e)
      return []
    })
}

/*
 * 予約連携設定
 * マーケットセグメント更新
 */
export const updateReservationMarketSegment = (marketSegments: Partial<MarketSegmentModel.MarketSegmentType>[]) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/reservation-market-segment`,
    data: { marketSegments },
  })
}

/**
 * 予約連携設定
 * タスク管理の対象予約の期間を取得
 * 失敗を握りつぶして既定値を返すと、画面が既定値を現在の設定として表示し、そのまま保存すると既存設定を上書きしてしまうため、エラーは呼び出し元へ返す
 */
export const fetchReservationTaskSetting = () => {
  return http<ReservationTaskSetting>({
    method: 'get',
    url: `${env('BASE_API_V3')}/reservation-task-setting`,
  }).then(results => results.data)
}

/**
 * 予約連携設定
 * タスク管理の対象予約の期間を更新
 */
export const updateReservationTaskSetting = (params: ReservationTaskSettingParams) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/reservation-task-setting`,
    data: params,
  })
}

/**
 * 宿泊管理 / タスク管理
 * 予約タスクの一覧を取得
 * 配列の条件は backend が `reservationStatuses[]=1` の形で受けるため brackets で送る。
 * 失敗を握りつぶして空の結果を返すと一覧が0件に見えるため、エラーは呼び出し元へ返す
 */
export const fetchReservationTasks = (params: ReservationTaskListParams) => {
  return http<ReservationTaskListResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/reservation-tasks`,
    params,
    paramsSerializer: query => qs.stringify(query, { arrayFormat: 'brackets' }),
  }).then(results => results.data)
}

/**
 * 宿泊管理 / タスク管理
 * タブのバッジに出す、未対応の予約タスクの件数を取得
 */
export const fetchReservationTaskUnhandledTotal = () => {
  return http<ReservationTaskUnhandledTotalResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/reservation-tasks/unhandled-total`,
  }).then(results => results.data)
}

/**
 * 宿泊管理 / タスク管理
 * 予約タスクの対応ステータス・対応スタッフを更新（1件の更新とまとめての更新の両方）
 */
export const updateReservationTasks = (params: ReservationTaskUpdateParams) => {
  return http({
    method: 'patch',
    url: `${env('BASE_API_V3')}/reservation-tasks`,
    data: params,
  })
}

/**
 * 前受金フラグを解除
 */
export const deleteSalesAdvancePayment = reservationId => {
  const params = { reservationId }
  return http<Aipass.DeleteSalesAccountsReceivableResponse>({
    method: 'delete',
    url: `${env('BASE_API_V3')}/sales-manager/sales-advance-payment`,

    params,
  }).then(results => results.data)
}

/**
 * 前受金フラグを付与
 */
export const updateSalesAdvancePayment = reservationIds => {
  const data = { reservationIds }
  return http<Aipass.PutSalesAccountsReceivableResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/sales-manager/sales-advance-payment`,
    data,
  }).then(results => results.data)
}

/**
 * Sun api fetch room prices
 */
export const fetchReservationRoomPrice = reservationId => {
  const params = { reservationId }

  return http<Aipass.GetReservationRoomPriceResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/reservation-room-price`,
    params,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

export const fetchReservationChannelCode = () => {
  return http<ChannelCodeType[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/reservation-channel-code`,
  })
    .then(results => results.data)
    .catch(e => {
      console.error(e)
      return []
    })
}

export const updateReservationChannelCode = (channelCodes: Partial<ChannelCodeType>[]) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/sales-manager/reservation-channel-code`,
    data: { channelCodes },
  })
}

export const importReservationChannelCode = rows => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/sales-manager/reservation-channel-code/import`,
    data: { rows },
  })
}

export const exportBookingData = exportDate => {
  const data = { export_date: exportDate }
  return http<Aipass.GetComExportRevCsvResponse>({
    method: 'post',
    url: `${env('BASE_API_V3')}/mark/booking-data`,
    data,
  }).then(results => results.data)
}

export const exportAccommodationData = exportDate => {
  const data = { export_date: exportDate }
  return http<Aipass.GetComExportRevCsvResponse>({
    method: 'post',
    url: `${env('BASE_API_V3')}/mark/accommodation-data`,
    data,
  }).then(results => results.data)
}

export const fetchPaymentSetting = () => {
  return http<{ settings: PaymentSettingType; terminals: PaymentTerminalSettingType }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/payment/setting`,
  }).then(results => results.data)
}

export const postPaymentSetting = setting => {
  return http<Aipass.PostPaymentSetting>({
    method: 'post',
    url: `${env('BASE_API_V3')}/payment/setting`,
    data: setting,
  }).then(results => results.data)
}

export const updatePaymentSetting = setting => {
  return http<Aipass.UpdatePaymentSetting>({
    method: 'put',
    url: `${env('BASE_API_V3')}/payment/setting`,
    data: setting,
  }).then(results => results.data)
}

export const issueExternalLinkPayment = (data: {
  reservationId?: string
  checkinId?: string
  filterGuestRoomAssignId: string[] | undefined
}) => {
  return http<{ amount: number; url: string }>({
    method: 'post',
    url: `${env('BASE_API_V3')}/payment/external-link-payment`,
    data,
  }).then(results => results.data)
}

export const fetchReservationsAndSales = (data: { hotelId: string; reservationIds: string[]; filterGuestRoomAssignIds?: string[] }) => {
  return http<Aipass.ReservationsAndSales>({
    method: 'post',
    url: `${env('BASE_API_V3')}/search-reservation`,
    data,
  }).then(results => results.data)
}

export const fetchReservationsForSelfCheckin = (params: SelfCheckInReservationSearchQuery) => {
  return http<SearchReservationTypeWhenNonCheckIn[] | SearchReservationTypeWhenCheckedIn[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/self-checkin/search-reservation`,
    params,
  }).then(results => results.data)
}

export const fetchSelfCheckOutReservations = (params: {
  hotelId: string
  telephone?: string | undefined
  reservationId?: string | undefined
  givenName?: string | undefined
  familyName?: string | undefined
  givenNameKana?: string | undefined
  familyNameKana?: string | undefined
  checkinId?: string | undefined
  roomNumber?: string | undefined
  keyNumber?: string | undefined
  checkoutDate?: string | undefined
  processId?: string | undefined
}) => {
  return http<{ stayReservations: SearchCheckOutReservation[] }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/stay-reservation`,
    params,
  }).then(results => results.data)
}

export const selfCheckinHandlePaymentByCash = (data: {
  hotelId: string
  reservationIdWithAssignIds: Array<{
    reservationId: string
    guestRoomAssignIds: string[]
  }>
  paymentPrice: number
}) => {
  return http<PaymentResultByCash>({
    method: 'post',
    url: `${env('BASE_API_V3')}/self-checkin/payment`,
    data,
  }).then(results => results.data)
}

export const selfCheckinHandlePaymentByCard = (data: {
  hotelId: string
  reservationIdWithAssignIds: Array<{
    reservationId: string
    guestRoomAssignIds: string[]
  }>
  cardToken: string
}) => {
  return http<PaymentResultByCredit>({
    method: 'post',
    url: `${env('BASE_API_V3')}/self-checkin/payment`,
    data,
  }).then(results => results.data)
}

export const sendMailSelfCheckin = (data: SuccessMailType) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/self-checkin/send-mail-success`,
    data,
  })
}

export const selfCheckinForReservations = data => {
  return http<{ checkinId: string; checkinCode: string; qrJson: string }>({
    method: 'post',
    url: `${env('BASE_API_V3')}/checkin-approved`,
    data,
  }).then(results => results.data)
}

export const fetchPaymentSettingByHotelId = hotelId => {
  return http<Aipass.SendMailSelfCheckin>({
    method: 'get',
    url: `${env('BASE_API_V3')}/payment-setting-by-hotel`,
    params: { hotelId },
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

export const selfCheckinUploadIdentificationImages = data => {
  return http<Aipass.SendMailSelfCheckin>({
    method: 'post',
    url: `${env('BASE_API_V3')}/self-checkin/identification-images`,
    data,
  }).then(results => results.data)
}

export const selfCheckinFetchGuestRoomAssign = (params: {
  hotelId: string
  reservationIdWithAssignIds: Array<{
    reservationId: string
    guestRoomAssignIds: string[]
  }>
  includeQrCode?: boolean
}) => {
  return http<{ rooms: Array<RoomInfoType> }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/self-checkin/guest-room-assign`,
    params,
    paramsSerializer: params => qs.stringify(params, { arrayFormat: 'indices' }),
  })
    .then(results => results.data.rooms)
    .catch(e => {
      console.error(e)
      return []
    })
}

export const selfCheckinFetchCardKeyRooms = (params: {
  hotelId: string
  reservationIdWithAssignIds: Array<{
    reservationId: string
    guestRoomAssignIds: string[]
  }>
}) => {
  return http<Array<RoomInfoType>>({
    method: 'get',
    url: `${env('BASE_API_V3')}/self-checkin/card-key-rooms`,
    params,
    paramsSerializer: params => qs.stringify(params, { arrayFormat: 'indices' }),
  })
    .then(results => results.data)
    .catch(e => {
      console.error(e)
      return []
    })
}

export const selfCheckinGuestRoomAutoAssign = (data: { hotelId: string; reservationIds: string[] }) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/self-checkin/guest-room-auto-assign`,
    data,
  })
}

export const selfCheckinRoomCleaningStatus = (data: { hotelId: string; guestRoomAssignIds: string[] }) => {
  return http<{ canCheckin: boolean }>({
    method: 'post',
    url: `${env('BASE_API_V3')}/self-checkin/room-cleaning-status`,
    data,
  }).then(results => results.data)
}

export const selfCheckinCreateReservation = data => {
  return http<Aipass.SendMailSelfCheckin>({
    method: 'post',
    url: `${env('BASE_API_V3')}/self-checkin/reservation`,
    data,
  }).then(results => results.data)
}

export const fetchZipCode = zipcode => {
  const params = { zipcode }
  return http<Aipass.FetchZipCode>({
    method: 'get',
    url: `${env('BASE_API_V3')}/zipcode-search`,
    params,
  }).then(results => results.data)
}

export const createAdvanceMoney = data => {
  return http<Aipass.AdvanceMoney>({
    method: 'post',
    url: `${env('BASE_API_V3')}/advance-money/store`,
    data,
  }).then(results => results.data)
}

export const fetchAdvanceMoneyHistoryByCompany = (companyId, page, limit) => {
  const params = { page, limit }
  return http<Aipass.AdvanceMoneyHistory>({
    method: 'get',
    url: `${env('BASE_API_V3')}/advance-money/${companyId}/history`,
    params,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

export const fetchAdvanceCompany = () => {
  return http<Aipass.AdvanceCompany>({
    method: 'get',

    url: `${env('BASE_API_V3')}/sales-manager/advance-companies`,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

export const fetchAdvanceMoney = (page: number, limit: number, search: AdvanceMoneySearchType) => {
  return http<Aipass.AdvanceMoney>({
    method: 'get',
    url: `${env('BASE_API_V3')}/advance-money`,
    params: {
      page,
      limit,
      ...search,
    },
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

export const deleteAdvanceMoneyCompany = companyId => {
  return http<Aipass.AdvanceMoney>({
    method: 'delete',

    url: `${env('BASE_API_V3')}/advance-money/${companyId}`,
  })
    .then(results => results.data)
    .catch(e => {
      throw new Error(e.response.data.errorMessage)
    })
}

export const exportAdvanceMoneyCompanyCsv = (params: AdvanceMoneySearchType) => {
  return http<Aipass.AdvanceMoney>({
    method: 'get',
    url: `${env('BASE_API_V3')}/advance-money/export-csv`,
    params,
    staffMutationTreatAsWrite: true,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

/**
 * MiwaLock
 * クライアント情報を取得
 */
export const updateMiwaCardIssuesLogs = data => {
  return http<Aipass.UpdateMiwaCardIssuesLogs>({
    method: 'put',
    url: `${env('BASE_API_V3')}/room-manager/miwa_card_issues_logs`,
    data,
  }).then(results => results.data)
}

/**
 * MiwaLock
 * 読み取ったカードキーの部屋番号・IN/OUT 日付から紐づく部屋割・予約を取得
 */
export const fetchMiwaCardKeyReservation = (params: TMiwaCardKeyReservationParams) => {
  return http<TMiwaCardKeyReservation>({
    method: 'get',
    url: `${env('BASE_API_V3')}/room-manager/miwa-card-key/reservation`,
    params,
  }).then(results => results.data)
}

/**
 * MiwaLock
 * クライアント情報を取得
 */
export const getMiwaAppWebsocketPort = pcId => {
  const params = { pcId }

  return http<Aipass.GetMiwaAppWebsocketPort>({
    method: 'get',
    url: `${env('BASE_API_V3')}/miwa-app-connection/get-app-port`,

    params,
  }).then(results => results.data)
}

/**
 * Dowload rev/cl csv
 */
export const fetchOldComExportRevCsv = (date_current_liability, type) => {
  const params = { date_current_liability, type }
  return http<{ url: string }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/com-download-csv`,
    params,
    staffMutationTreatAsWrite: true,
  }).then(results => results.data)
}

export const fetchUsedCouponsList = ({ beginDate, endDate, page, limit = 50 }) => {
  return http({
    method: 'get',
    url: `${env('BASE_API_V3')}/used-coupons`,
    headers: { Authorization: getIdToken() },
    params: {
      beginDate: beginDate ? moment(beginDate).unix() : undefined,
      endDate: endDate ? moment(endDate).unix() : undefined,
      page,
      limit,
    },
  }).then(results => results.data)
}

export const exportReport = (data: Partial<ReportFormType>) => {
  return http<{ url: string }>({
    method: 'post',
    url: `${env('BASE_API_V3')}/sales-manager/report/export`,
    data,
  }).then(results => results.data)
}

export const getReservationsBoundaryDates = () => {
  return http<Aipass.GetSalesManagerSalesCsvResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/reservations-boundary-dates`,
  }).then(results => results.data)
}

export const adminCheckinApprovedTabletRegister = data => {
  return http<Aipass.GetSalesManagerSalesCsvResponse>({
    method: 'put',
    url: `${env('BASE_API_V3')}/admin-checkin-approved-tablet-register`,
    data,
  }).then(results => results.data)
}

export const guestRoomFix = data => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/room-manager/guest-room-fix`,
    data,
  }).then(results => results.data)
}

export const fetchAutoAssignmentSettings = () => {
  return http<GuestRoomAutoAssignConfig>({
    method: 'get',
    url: `${env('BASE_API_V3')}/auto-assignment-settings`,
  }).then(results => results.data)
}

export const applyAutoAssignmentSettings = (data: Omit<GuestRoomAutoAssignConfig, 'timingOfDatetimeSetting'>) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/auto-assignment-settings`,
    data,
  })
}

export const saveAutoAssignmentSettings = (data: Pick<GuestRoomAutoAssignConfig, 'timingOfDatetimeSetting'>) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/auto-assignment-settings`,
    data,
  })
}

export const guestRoomAutoAssign = data => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/room-manager/guest-room-auto-assign`,
    data,
  }).then(results => results.data)
}

export const userGuestCopy = data => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/admin-reservation-user-guest-copy`,
    data,
  }).then(results => results.data)
}

export const fetchSmaregiTerminals = () => {
  return http<Array<TerminalType>>({
    method: 'get',
    url: `${env('BASE_API_V3')}/smaregi/setting/terminals`,
  }).then(results => results.data)
}

export const postSmaregiTransactionsLayaways = (data: { contractId: string; storeId: string; terminalId: string; saleIds: string[] }) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/smaregi/transactions/layaways`,
    data,
  })
}

export const fetchSmaregiTransactions = (data: { transactionAt: Dayjs }) => {
  return http<Array<TransactionType>>({
    method: 'get',
    url: `${env('BASE_API_V3')}/smaregi/transactions`,
    params: { transactionDate: data.transactionAt.format('YYYY-MM-DD') },
  }).then(results => results.data)
}

export const fetchSmaregiTransactionDetail = (transactionId: string) => {
  return http<TransactionType>({
    method: 'get',
    url: `${env('BASE_API_V3')}/smaregi/transactions/${transactionId}`,
  }).then(results => results.data)
}

export const putSmaregiTransactionLinkReservation = (
  transactionId: string,
  data: {
    reservationId: string
    roomNumber: string
  },
) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/smaregi/transactions/${transactionId}/link-reservation`,
    data,
  })
}

export const getEmployeeHotels = () => {
  return http<EmployeeHotel[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/employee-hotels`,
  }).then(results => results.data)
}

export const getEmployeeHotelPermissions = () => {
  return http<HotelPmsPermissions>({
    method: 'get',
    url: `${env('BASE_API_V3')}/employee-hotels/permissions`,
  }).then(results => results.data)
}

export const setEmployeeCurrentHotel = (currentHotelId: string) => {
  return http({
    method: 'patch',
    url: `${env('BASE_API_V3')}/employee-hotels/current`,
    data: { currentHotelId },
  }).then(results => results.data)
}

export const getStaffList = () => {
  return http({
    method: 'get',
    url: `${env('BASE_API_V3')}/staff-manager`,
  }).then(results => results.data)
}

export const createStaff = (data: StaffInputType) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/staff-manager`,
    data,
  }).then(results => results.data)
}

export const updateStaff = (staffId: string | number, data: StaffInputType) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/staff-manager/${staffId}`,
    data,
  }).then(results => results.data)
}

export const deleteStaff = (staffId: string | number) => {
  return http({
    method: 'delete',
    url: `${env('BASE_API_V3')}/staff-manager/${staffId}`,
  }).then(results => results.data)
}

export const getEmployeeStaff = () => {
  return http({
    method: 'get',
    url: `${env('BASE_API_V3')}/staff`,
  }).then(results => results.data)
}

export const loginEmployeeStaff = (currentStaffId: string | number, passCode?: string) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/staff/login`,
    data: {
      currentStaffId,
      passCode,
    },
  }).then(results => results.data)
}

export const logoutEmployeeStaff = () => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/staff/logout`,
  }).then(results => results.data)
}

/** バッチ要約一覧（GET .../operation-log/v2） */
export const getOperationLogV2List = ({
  beginDate,
  endDate,
  reservationId,
  staff,
  employee,
  featureSlug,
  table,
  afterClosingAdjustment,
  page = 1,
  limit = 50,
}: OperationLogFiltersType & { page?: number; limit?: number }): Promise<OperationLogListResponse> => {
  const params: Record<string, unknown> = {
    beginDate,
    endDate,
    page,
    limit,
  }
  if (reservationId != null && reservationId !== '') params.reservationId = reservationId.trim()
  if (staff != null) params.staff = staff
  if (employee != null && employee !== '') params.employee = employee
  if (featureSlug != null && featureSlug !== '') params.feature_slug = featureSlug
  if (table != null && table !== '') params.table = table
  if (afterClosingAdjustment) params.after_closing_adjustment = 1
  return http<OperationLogListResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/operation-log/v2`,
    params,
  }).then(results => results.data)
}

/** レガシー activity 一覧（GET .../operation-log）。日付・contents は任意 */
export const getOperationLogLegacyList = (
  arg: OperationLogLegacyFiltersType & { page?: number; limit?: number },
): Promise<OperationLogLegacyListResponse> => {
  const { page = 1, limit = 50, beginDate, endDate, contents, reservationId, staff, employee, features, table } = arg
  const params: Record<string, unknown> = { page, limit }
  if (beginDate != null && String(beginDate).trim() !== '') params.beginDate = beginDate
  if (endDate != null && String(endDate).trim() !== '') params.endDate = endDate
  if (contents != null && String(contents).trim() !== '') params.contents = String(contents).trim()
  if (reservationId != null && reservationId !== '') params.reservationId = reservationId.trim()
  if (staff != null) params.staff = staff
  if (employee != null && employee !== '') params.employee = employee
  if (features != null && features !== '') params.features = features
  if (table != null && table !== '') params.table = table
  return http<OperationLogLegacyListResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/operation-log`,
    params,
  }).then(results => results.data)
}

/** 1 activity 単位の詳細（GET …/operation-log/{id}）。現行 UI では未使用だが API 互換のため残す。 */
export const getOperationLogDetail = (id: number) => {
  return http<OperationLogDetailType>({
    method: 'get',
    url: `${env('BASE_API_V3')}/operation-log/${id}`,
  }).then(results => results.data)
}

export const getOperationLogBatch = (uuid: string) => {
  return http<OperationLogBatchDetailType | { data: OperationLogBatchDetailType }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/operation-log/batch/${uuid}`,
  }).then(results => {
    const payload = results.data as OperationLogBatchDetailType | { data?: OperationLogBatchDetailType }
    if (
      payload &&
      typeof payload === 'object' &&
      'data' in payload &&
      payload.data &&
      typeof payload.data === 'object' &&
      'batch_uuid' in payload.data
    ) {
      return payload.data
    }
    return payload as OperationLogBatchDetailType
  })
}

/** GET /employee-hotels/employees は { data } wrap ではなく配列本体を返す */
export const getHotelEmployeeList = (): Promise<EmployeeType[]> => {
  return http<EmployeeType[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/employee-hotels/employees`,
  }).then(results => (Array.isArray(results.data) ? results.data : []))
}

export const getAssetList = () => {
  return http<AssetType[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/guest-app/assets`,
  })
    .then(results => results.data)
    .catch(e => {
      console.log({ e })
      return []
    })
}
export const getEnabledAssetType = () => {
  return http<{ formatTypes: FormatType[] }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/guest-app/assets/type`,
  })
    .then(results => results.data.formatTypes)
    .catch(e => {
      console.log({ e })
      return []
    })
}

export const createAsset = (data: { title: string; formatType: FormatType }) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/guest-app/assets/`,
    data,
  })
}

export const updateAsset = (id: string, data: { title?: string; changeVisible?: boolean }) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/guest-app/assets/${id}`,
    data,
  })
}

export const deleteAsset = (id: string[]) => {
  return http({
    method: 'delete',
    url: `${env('BASE_API_V3')}/guest-app/assets/bulk-delete`,
    params: { id },
  })
}

export const updateAssetDisplayOrder = (sortedIds: string[]) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/guest-app/assets/display-order`,
    data: { sortedIds },
  })
}

export const createAssetDetail = (
  id: string,
  type: FormatType,
  details:
    | AssetFreeDetailFormValue
    | AssetMapDetailFormValue
    | AssetNewsDetailFormValue
    | AssetCouponDetailFormValue
    | AssetProductDetailFormValue
    | SpaceCreateAndUpdateRequestType,
) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/guest-app/assets/${id}/detail`,
    data: {
      type,
      details,
    },
  })
}

export const updateAssetDetail = (
  id: string,
  detailId: string,
  type: FormatType,
  details:
    | AssetFreeDetailFormValue
    | AssetPulldownDetailFormValue
    | Partial<AssetMapDetailFormValue>
    | AssetNewsDetailFormValue
    | AssetButtonFormValue
    | AssetImageDetailFormValue
    | AssetCouponDetailFormValue
    | AssetProductDetailFormValue
    | SpaceCreateAndUpdateRequestType,
) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/guest-app/assets/${id}/detail/${detailId}`,
    data: {
      type,
      details,
    },
  })
}

export const deleteAssetDetail = (id: string, deleteDetailIds: string[] | number[]) => {
  return http({
    method: 'delete',
    url: `${env('BASE_API_V3')}/guest-app/assets/${id}/detail/bulk-delete`,
    params: { id: deleteDetailIds },
  })
}

export const updateAssetDetailDisplayOrder = (id: string, sortedIds: string[] | number[]) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/guest-app/assets/${id}/detail/display-order`,
    data: { sortedIds },
  })
}

export const getAssetMapCategoryList = (assetId: string) => {
  return http<MapCategoryType[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/guest-app/assets/${assetId}/map-categories`,
  })
    .then(results => results.data)
    .catch(e => {
      console.error(e)
      return []
    })
}

export const updateAssetMapCategory = (assetId: string, id: string, data: { title: string; imagePath: string }) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/guest-app/assets/${assetId}/map-categories/${id}`,
    data,
  })
}

export const updateAssetMapCategoryDisplayOrder = (assetId: string, sortedIds: string[]) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/guest-app/assets/${assetId}/map-categories/display-order`,
    data: { sortedIds },
  })
}

export const updateAssetRequestCategoryDisplayOrder = (assetId: string, sortedIds: number[]) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/guest-app/assets/${assetId}/request-categories/display-order`,
    data: { sortedIds },
  })
}

export const updateAssetRequestTagDisplayOrder = (assetId: string, sortedIds: number[]) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/guest-app/assets/${assetId}/request-tags/display-order`,
    data: { sortedIds },
  })
}

export const updateAssetRequestSalesHourDisplayOrder = (assetId: string, sortedIds: number[]) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/guest-app/assets/${assetId}/request-sales-hours/display-order`,
    data: { sortedIds },
  })
}

export const getDesignTheme = () => {
  return http<DesignType>({
    method: 'get',
    url: `${env('BASE_API_V3')}/guest-app/designs`,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

export const previewDesignTheme = (params: ThemeType) => {
  return http<DesignType>({
    method: 'get',
    url: `${env('BASE_API_V3')}/guest-app/designs/preview`,
    params,
  })
    .then(results => results.data)
    .catch(e => console.error(e))
}

export const updateDesignTheme = (data: ThemeType) => {
  return http<DesignType>({
    method: 'put',
    url: `${env('BASE_API_V3')}/guest-app/designs`,
    data,
  })
}

export const getMailDeliveryTemplates = (params: { method: DeliveryMethod }) => {
  return http<TemplateContent[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/mail-delivery-templates`,
    params,
  })
    .then(result => result.data)
    .catch(e => {
      console.error(e)
      return []
    })
}

export const getMailDeliveryContents = () => {
  return http<DeliveryContentItem[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/mail-delivery-contents`,
  })
    .then(result => result.data)
    .catch(e => {
      console.error(e)
      return []
    })
}

export const getMailDeliveryContentDetail = (id: string) => {
  return http<MailDeliveryContent>({
    method: 'get',
    url: `${env('BASE_API_V3')}/mail-delivery-content/${id}`,
  }).then(result => result.data)
}

export const saveMailDeliveryContent = (method: DeliveryMethod, data: MailDeliveryContentInput) => {
  return http<{ id: string }>({
    method: 'post',
    url: `${env('BASE_API_V3')}/mail-delivery-content`,
    data: {
      ...data,
      method,
      isSendToAfterDelivery: data.isSendToAfterDelivery || false,
      isNullSearchableChannelCode: data.channelCodes.includes('-'),
      channelCodes: data.channelCodes.filter(code => code !== '-') ?? [],
      reservationStatuses: data.reservationStatuses ? data.reservationStatuses.map(Number) : [],
    },
  }).then(result => result.data)
}

export const updateMailDeliveryContent = (id: string, data: MailDeliveryContentInput) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/mail-delivery-content/${id}`,
    data: {
      ...data,
      isSendToAfterDelivery: data.isSendToAfterDelivery || false,
      isNullSearchableChannelCode: data.channelCodes.includes('-'),
      channelCodes: data.channelCodes.filter(code => code !== '-') ?? [],
      reservationStatuses: data.reservationStatuses ? data.reservationStatuses.map(Number) : [],
    },
  })
}

export const deleteMailDeliveryContents = (params: { ids: string[] }) => {
  return http({
    method: 'delete',
    url: `${env('BASE_API_V3')}/mail-delivery-contents/bulk-delete`,
    params,
  })
}

export const sendTestMailDeliveryContent = (method: DeliveryMethod, testSendTo: string, data: MailDeliveryContentInput) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/mail-delivery-content/send-test`,
    data: {
      method,
      testSendTo,
      title: data.title,
      content: data.content,
    },
  })
}

export const getMailDeliverySetting = () => {
  return http<{ replyEmail: string; hasForeignSMS: boolean }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/mail-delivery-setting`,
  }).then(result => result.data)
}

export const updateMailDeliverySetting = (data: { replyEmail: string }) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/mail-delivery-setting`,
    data,
  })
}

export const getMailDeliveryHistory = (params: GetMailDeliveryHistoryParams) => {
  return http<MailDeliveryHistoryResult>({
    method: 'get',
    url: `${env('BASE_API_V3')}/mail-delivery-histories`,
    params,
  }).then(result => result.data)
}

export const getMailDeliverySendResults = (id: string) => {
  return http<MailDeliverySendResultsResult>({
    method: 'get',
    url: `${env('BASE_API_V3')}/mail-delivery-history/${id}/send-results`,
  }).then(result => result.data)
}

export const CreateSpaceReservationFromSelfCI = (data: ReservationForSelfCheckinInput) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/guest/spaces/reservations-from-self-ci`,
    data,
  })
}

export const FixSpaceReservationFromSelfCI = (data: { hotelId: string; reservationIdWithAssignIds: SelectedReservationAndRooms[] }) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/guest/spaces/reservations/provisional/success`,
    data,
  })
}

export const CancelSpaceReservationFromSelfCI = (data: { hotelId: string; reservationIdWithAssignIds: SelectedReservationAndRooms[] }) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/guest/spaces/reservations/provisional/cancel`,
    data,
  })
}

export const CreateSpaceReservation = (data: AlignmentReservationInput | NewReservationInput) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/spaces/reservations`,
    data,
  }).then(result => result.data)
}

export const updateSpaceReservation = (id: string, data: { assignSpaces: UpdateSpaceReservationInput[] }) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/spaces/reservations/${id}`,
    data,
  }).then(result => result.data)
}

export const getReservationSpaces = (params: { day: string }) => {
  return http<SpaceReservation[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/spaces/reservations`,
    params,
  }).then(result => result.data)
}
export const exportReservationSpaces = (params: { day: string; filterAssetId: string[]; noteCategory: ReservationNoteCategory }) => {
  return http<{ url: string }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/spaces/reservations/pdf`,
    params,
    staffMutationTreatAsWrite: true,
  }).then(result => result.data)
}
export const getSpaceByReservation = (reservationId: string) => {
  return http<SpaceReservation[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/spaces/reservations`,
    params: { reservationId },
  })
    .then(result => result.data)
    .catch(e => console.error(e))
}
export const putSpaceAssignRoomKey = (data: {
  assign: { spaceId: string; spaceIndex: number; keyNumber: string | undefined; remoteDeviceId: string | undefined }[]
}) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/smart-lock/assign-key/spaces`,
    data,
  }).then(result => result.data)
}

export const getSalesInvoice = (reservationIds: string[]) => {
  return http<SalesInvoice.SalesType[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/invoices/sales`,
    params: { reservationIds },
  })
    .then(result => result.data)
    .catch(e => {
      console.error(e)
      return []
    })
}

export const getInvoice = (params?: SalesInvoice.InvoiceSearchType) => {
  return http<{
    total: number
    hasPrev: boolean
    hasNext: boolean
    result: SalesInvoice.InvoiceType[]
  }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/invoices`,
    params,
  })
    .then(result => result.data)
    .catch(e => console.error(e))
}

export const createInvoice = (data: {
  clientName: string
  honorific: string
  paymentDate: string
  note: string
  isIssueQuotation: boolean
  salesIds: string[]
}) => {
  return http<{ invoiceUrl: string; quotationUrl: string | null }>({
    method: 'post',
    url: `${env('BASE_API_V3')}/invoices`,
    data,
  }).then(result => result.data)
}

export const changeInvoiceStatus = (
  invoiceId: string,
  data: {
    status: SalesInvoice.InvoiceStatusType
    isConfirmed?: boolean
    isForceUpdate?: boolean
    paymentAt: Dayjs | undefined
  },
) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/invoices/${invoiceId}/status`,
    data: {
      ...data,
      paymentAt: data.paymentAt?.format('YYYY-MM-DD HH:mm:ss'),
    },
  })
}

export const getSalesManagerDashboard = (params: { mode: 'daily' | 'monthly'; targetDay: Dayjs; filter: DashboardSalesFilterType }) => {
  return http<DashboardSalesType>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/dashboard`,
    params: {
      mode: params.mode,
      targetDay: params.targetDay.format('YYYY-MM-DD'),
      'filter.salesType.other': params.filter.salesType.other,
      'filter.salesType.room': params.filter.salesType.room,
      'filter.salesType.temporary': params.filter.salesType.temporary,
      'filter.stayType.dayUse': params.filter.stayType.dayUse,
      'filter.stayType.stay': params.filter.stayType.stay,
      'filter.isTaxIncluded': params.filter.isTaxIncluded,
    },
  })
    .then(result => result.data)
    .catch(e => {
      console.error(e)
      return undefined
    })
}

export const getLatestConfirmedDate = () => {
  return http<{ latestConfirmedDate: string | null }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/latest-confirmed-date`,
  })
    .then(result => result.data)
    .catch(e => {
      console.error(e)
      return { latestConfirmedDate: null }
    })
}

/** 締め後修正モーダル「入金営業日」ピッカー用の選択可能日一覧 */
export const fetchSelectablePaymentBusinessDates = () => {
  return http<SelectablePaymentBusinessDatesResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/selectable-payment-business-dates`,
  }).then(result => result.data)
}

export const getSmartLockAccount = () => {
  return http<Array<AccountInfo>>({
    method: 'get',
    url: `${env('BASE_API_V3')}/smart-lock`,
  })
    .then(result => result.data)
    .catch(e => {
      console.error(e)
      return []
    })
}

export const getSmartLockDevices = (params: { lockType: LockType }) => {
  return http<Array<Device>>({
    method: 'get',
    url: `${env('BASE_API_V3')}/smart-lock/devices`,
    params,
  })
    .then(result => result.data)
    .catch(e => {
      console.error(e)
      return []
    })
}

export const getLockSettings = () => {
  return http<LockSetting[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/smart-lock/lock-settings`,
  })
    .then(result => result.data)
    .catch(e => {
      console.error(e)
      return []
    })
}

export const updateLockSettings = (data: { roomKind: RoomKindType; lockType: LockType } & ApiCredential) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/smart-lock/lock-settings`,
    data,
  }).then(result => result.data)
}

export const exportSalesAccountingReport = (params: {
  from: string
  to: string
  formatType: SalesAccountingModel.FormatType
  systemType: SalesAccountingModel.SystemType
  dateRangeType: SalesAccountingModel.DateRangeType
}) => {
  return http<{ path: string | undefined }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/sales-accounting/export`,
    params,
    staffMutationTreatAsWrite: true,
  })
    .then(result => result.data)
    .catch(e => {
      console.error(e)
      return { path: undefined }
    })
}

export const fetchCustomerTagMasters = () => {
  return http<CustomerTagMasterType[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/customer-tag-master`,
  })
    .then(result => result.data)
    .catch(e => console.error(e))
}

export const createCustomerTagMaster = (data: CustomerTag) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/customer-tag-master`,
    data,
  }).then(result => result.data)
}

export const deleteCustomerTagMaster = (ids: string[]) => {
  return http({
    method: 'delete',
    url: `${env('BASE_API_V3')}/customer-tag-master/bulk-delete`,
    params: { ids },
  }).then(result => result.data)
}

export const updateCustomerTagMaster = (data: CustomerTag, id: string) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/customer-tag-master/${id}`,
    data,
  }).then(result => result.data)
}

export const updateCustomerTagMasterDisplayOrder = (sortedIds: string[]) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/customer-tag-masters/display-order`,
    data: { sortedIds },
  })
}

export const createCustomerTag = (data: { customerId: string; customerTagMasterIds: string[] }) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/customer-tag`,
    data,
  }).then(result => result.data)
}

export const deleteCustomerTag = (params: { customerId: string; customerTagMasterId: string }) => {
  return http({
    method: 'delete',
    url: `${env('BASE_API_V3')}/customer-tag`,
    params,
  }).then(result => result.data)
}

export const postSelfCheckinCardReader = (
  hotelId: string,
  serialNumber: string,
  pcId: string | undefined | null,
  command: CardReaderCommand,
  parentProcessId?: string,
) => {
  return http<{ processId: string }>({
    method: 'post',
    url: `${env('BASE_API_V3')}/self-checkin/card-reader/command/send`,
    data: { hotelId, serialNumber, pcId, parentProcessId, command },
  }).then(result => result.data)
}

export const postSelfCheckinCardReaderWithCheckin = (
  hotelId: string,
  serialNumber: string,
  pcId: string | undefined | null,
  command: CardReaderCommand,
  checkin: {
    guestRoomAssignId: string
    checkinId: string
    checkinDate: string
    checkoutDate: string
    roomNumber: number
    numberOfCards: number
    branchNumber: number
  },
) => {
  return http<{ processId: string }>({
    method: 'post',
    url: `${env('BASE_API_V3')}/self-checkin/card-reader/command/send`,
    data: { hotelId, serialNumber, pcId, command, ...checkin },
  }).then(result => result.data)
}

export const getSelfCheckinCardReader = (hotelId: string, serialNumber: string, pcId: string | undefined | null, processId: string) => {
  return http<SelfCheckinCardReaderType[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/self-checkin/card-reader/command/fetch`,
    params: { hotelId, serialNumber, pcId, processId },
  })
    .then(result => result.data)
    .catch(e => {
      console.error(e)
      return undefined
    })
}

export const getKeywordGroupSetting = () => {
  return http<KeywordGroup[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/plan-keyword-group`,
  })
    .then(result => result.data)
    .catch(e => {
      console.error(e)
      return []
    })
}
export const storeKeywordGroupSetting = (formValue: KeywordGroup) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/plan-keyword-group`,
    data: formValue,
  })
}
export const updateKeywordGroupSetting = (id: string, formValue: KeywordGroup) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/plan-keyword-group/${id}`,
    data: formValue,
  })
}
export const deleteKeywordGroupSetting = (ids: string[]) => {
  return http({
    method: 'delete',
    url: `${env('BASE_API_V3')}/plan-keyword-group/bulk-delete`,
    params: { ids },
  })
}
export const sortKeywordGroupSetting = (ids: string[]) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/plan-keyword-group/display-order`,
    data: { ids },
  })
}
/** スタッフ用。ログイン中の施設と hotelId が一致しないと 403 になる */
export const getUploadKey = (params: { isPublic: boolean; feature: string; hotelId: string }) => {
  return http<UploadKey>({
    method: 'get',
    url: `${env('BASE_API_V3')}/s3-upload-key`,
    params,
  }).then(result => result.data)
}

/**
 * セルフチェックインの身分証用。予約 UUID で発行可否が判定される（予約なしは施設条件で判定）。
 * 非公開ストレージ固定。
 */
export const getSelfCheckinUploadKey = (params: { feature: string; hotelId: string; reservationUuid?: string }) => {
  return http<UploadKey>({
    method: 'get',
    url: `${env('BASE_API_V3')}/auth/s3-upload-key`,
    params: { ...params, isPublic: false },
  }).then(result => result.data)
}

export const storeVescaHistory = (data: {
  hotelId: string
  host: string
  sequenceNumber: number | undefined
  direction: 'request' | 'response'
  command: string
  amount: number | undefined
  message: object
  checkinId: string | undefined
  reservationIds?: string[] | undefined
  guestRoomAssignIds?: string[] | undefined
  salesIds?: string[] | undefined
}) => {
  return http<{
    result?: OutputCompleteEvent['pmsResult']
  }>({
    method: 'post',
    url: `${env('BASE_API_V3')}/payment/vesca/histories`,
    data,
  }).then(result => result.data)
}

export const stateLog = data => {
  return http({ method: 'post', url: `${env('BASE_API_V3')}/state-log`, data }).catch(e => console.warn(e))
}
export const miwaAppLog = data => {
  return http({ method: 'post', url: `${env('BASE_API_V3')}/miwa-app-log`, data }).catch(e => console.warn(e))
}
export const printerLog = data => {
  return http({ method: 'post', url: `${env('BASE_API_V3')}/printer-log`, data }).catch(e => console.warn(e))
}
export const cashChangerLog = data => {
  return http({ method: 'post', url: `${env('BASE_API_V3')}/cash-changer-log`, data }).catch(e => console.warn(e))
}
export const debugState = (debugName: string, data: any) => {
  return http({ method: 'post', url: `${env('BASE_API_V3')}/state-log/${debugName}`, data }).catch(e => console.warn(e))
}

export const fetchReservationCustomExportSetting = () => {
  return http<{
    allColumns: Array<CustomExport.Column>
    storedColumns: Array<CustomExport.Setting>
  }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/accommodation-management-custom-export/setting`,
    staffMutationTreatAsWrite: true,
  }).then(result => result.data)
}

export const updateReservationCustomExportSetting = (id: string, data: { title: string }) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/accommodation-management-custom-export/setting/${id}`,
    data,
  })
}

export const deleteReservationCustomExportSetting = (id: string) => {
  return http({
    method: 'delete',
    url: `${env('BASE_API_V3')}/accommodation-management-custom-export/setting/${id}`,
  })
}

export const postReservationCustomExport = (data: {
  setting: {
    doSave: boolean
    id?: string
    title?: string
    columns: string[]
  }
  search: any
}) => {
  return http<Array<CustomExport.Setting>>({
    method: 'post',
    url: `${env('BASE_API_V3')}/accommodation-management-custom-export`,
    data,
  })
}

export const fetchCustomScriptForPMS = () => {
  return http<Array<{ script: string }>>({
    method: 'get',
    url: `${env('BASE_API_V3')}/custom-script/hotel`,
  }).then(result => result.data)
}

export const fetchCustomScriptForSelfCheckin = (params: { hotelId: string }) => {
  return http<Array<{ script: string }>>({
    method: 'get',
    url: `${env('BASE_API_V3')}/custom-script/self-checkin`,
    params,
  }).then(result => result.data)
}

export const fetchAccountsReceivableForManager = (params: AccountsRreceivableMananger.AccountsReceivableSearchCondition) => {
  return http<AccountsRreceivableMananger.FetchAccountsReceivableResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/accounts-receivable-manager/accounts-receivable`,
    params,
  })
    .then(result => result.data)
    .catch(e => {
      console.error(e)
      return undefined
    })
}
export const exportAccountsReceivableForManager = (params: AccountsRreceivableMananger.AccountsReceivableSearchCondition) => {
  return http<{ url: string }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/accounts-receivable-manager/accounts-receivable/export`,
    params,
    staffMutationTreatAsWrite: true,
  }).then(result => result.data)
}
export const fetchAccountsReceivableBalanceForManager = (params: { salesDate: string }) => {
  return http<{
    indicator: { prevBalancePrice: number; accountsReceivablePrice: number; currentReceiptPrice: number; currentBalancePrice: number }
    results: AccountsRreceivableMananger.AccountsReceivableBalance[]
  }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/accounts-receivable-manager/accounts-receivable-balance`,
    params,
  })
    .then(result => result.data)
    .catch(e => {
      console.error(e)
      return undefined
    })
}
export const exportAccountsReceivableBalanceForManager = (params: { salesDate: string }) => {
  return http<{ url: string }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/accounts-receivable-manager/accounts-receivable-balance/export`,
    params,
    staffMutationTreatAsWrite: true,
  }).then(result => result.data)
}
export const fetchAccountsReceivableReceiptForManager = (params: { receiptDate: string }) => {
  return http<{
    indicator: { receiptPrice: number }
    results: AccountsRreceivableMananger.AccountsReceivableReceipt[]
  }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/accounts-receivable-manager/accounts-receivable-receipt`,
    params,
  })
    .then(result => result.data)
    .catch(e => {
      console.error(e)
      return undefined
    })
}
export const exportAccountsReceivableReceiptForManager = (params: { receiptDate: string }) => {
  return http<{ url: string }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/accounts-receivable-manager/accounts-receivable-receipt/export`,
    params,
    staffMutationTreatAsWrite: true,
  }).then(result => result.data)
}
export const receivableReconciliationForManager = (data: {
  saleIds: string[]
  discountIds: string[]
  receipts: AccountsRreceivableMananger.InputReceipts[]
}) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/accounts-receivable-manager/accounts-receivable/reconciliation`,
    data,
  })
}

export const postIdentificationVerify = (data: {
  hotelId: string
  isManual: boolean
  imagePath: string
  idType: 'drivingLicenseFront' | 'drivingLicenseBack' | 'passport'
}) => {
  return http<{
    verify: boolean
    nationality: string | undefined
  }>({
    method: 'post',
    url: `${env('BASE_API_V3')}/identification/verify`,
    data,
  }).then(result => result.data)
}

export const fetchSalesManagerDailySummary = (params: { salesManagerDailyId: string }) => {
  return http<DailySummary>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/daily-summary`,
    params,
  })
    .then(result => result.data)
    .catch(e => {
      console.error(e)
      return undefined
    })
}
export const fetchSalesManagerMonthlySummary = (params: { salesManagerMonthId: string }) => {
  return http<MonthlySummary>({
    method: 'get',
    url: `${env('BASE_API_V3')}/sales-manager/monthly-summary`,
    params,
  })
    .then(result => result.data)
    .catch(e => {
      console.error(e)
      return undefined
    })
}

export const tabletCheckout = (data: {
  hotelId: string
  language: string
  checkinId: string
  paymentResult?: {
    receiptName: string
    orderId: string
    paymentMethod: string
    paymentMethods?: PaymentMethodInput[]
  }
  reservations: Array<{
    reservationId: string
    guestRoomAssignIds: string[]
  }>
}) => {
  return http<selfCheckoutModel.SelfCheckOutState['complete']>({
    method: 'post',
    url: `${env('BASE_API_V3')}/tablet/tablet-checkout`,
    data,
  }).then(result => result.data)
}

export const sendMailForTabletCheckout = (data: {
  hotelId: string
  checkinId: string
  paymentResult: {
    receiptName: string
    orderId: string
    paymentMethod: string
  }
  reservations: Array<{
    reservationId: string
    guestRoomAssignIds: string[]
  }>
}) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/tablet/tablet-checkout/receipt`,
    data,
  })
}

export const paymentTabletCheckoutByCredit = (data: {
  hotelId: string
  reservations: Array<{
    reservationId: string
    guestRoomAssignIds: string[]
  }>
  cardToken: string
}) => {
  return http<selfCheckoutModel.PaymentResultByCredit>({
    method: 'put',
    url: `${env('BASE_API_V3')}/tablet/tablet-checkout/payment`,
    data,
  }).then(result => result.data)
}
export const paymentTabletCheckoutByCash = (data: {
  hotelId: string
  reservations: Array<{
    reservationId: string
    guestRoomAssignIds: string[]
  }>
  paymentPrice: number
}) => {
  return http<selfCheckoutModel.PaymentResultByCash>({
    method: 'put',
    url: `${env('BASE_API_V3')}/tablet/tablet-checkout/payment`,
    data,
  }).then(result => result.data)
}

export const fetchTabletCheckoutSettings = (hotelId: string) => {
  return http<SearchSetting[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/tablet/tablet-checkout/settings`,
    params: { hotelId },
  }).then(result => result.data)
}

export const fetchTabletCheckoutEnabled = (hotelId: string) => {
  return http<{ tabletCheckoutEnabled: boolean }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/tablet/tablet-checkout/enabled`,
    params: { hotelId },
  }).then(result => result.data)
}

export const fetchTabletCheckoutPrint = (params: { hotelId: string; checkinId: string; reservationIds?: string[] }) => {
  return http({
    method: 'get',
    url: `${env('BASE_API_V3')}/tablet/tablet-checkout/print`,
    params,
    paramsSerializer: params => qs.stringify(params),
  }).then(results => results.data)
}

export const fetchAdminTabletCheckoutSettings = () => {
  return http<selfCheckoutSettingModel.SettingResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/tablet-checkout/settings`,
  }).then(result => result.data)
}

// 予約検索項目のソート順変更
export const putTabletCheckoutSearchSettingSortOrder = (data: { searchItems: SearchSetting[] }) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/tablet-checkout-search-setting/order`,
    data,
  }).then(results => results.data)
}

// 予約検索項目の表示/非表示変更
export const putTabletCheckoutSearchSettingIsEnabled = (data: SearchSetting) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/tablet-checkout-search-setting/enabled`,
    data,
  }).then(results => results.data)
}

// 完了画面メッセージ更新
export const updateCheckoutMessages = (data: { complete: string; keyReturn: string; frontSupport: string }) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/tablet-checkout/messages`,
    data,
  }).then(result => result.data)
}

// アンケートQRコード設定更新
export const updateSurveyQrcodeEnabled = (data: { surveyQrcodeEnabled: boolean }) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/tablet-checkout/survey-qrcode`,
    data,
  }).then(result => result.data)
}

// タブレットチェックアウト有効/無効更新
export const updateTabletCheckoutEnabledAPI = (data: { tabletCheckoutEnabled: boolean }) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/tablet-checkout/enabled`,
    data,
  }).then(result => result.data)
}

export const validateReservationDate = (data: {
  reservationId: string
  checkinId?: string | null
  checkinDate: string
  checkoutDate: string
}) => {
  return http<{ valid: boolean }>({
    method: 'post',
    url: `${env('BASE_API_V3')}/reservation-date-validation`,
    data,
  }).then(results => results.data)
}

// Survey APIs
export const getSurveyNpsSummary = (params: NpsSummaryParams = {}) => {
  return http<NpsSummaryResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-surveys/nps-summary`,
    params,
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: 'brackets' })
    },
  }).then(result => result.data)
}

export const getSurveyStatistics = (params: SurveyStatisticsParams = {}) => {
  return http<SurveyStatisticsResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-surveys/statistics`,
    params,
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: 'brackets' })
    },
  }).then(result => result.data)
}

export const getSurveyAnswers = (params: SurveyAnswersParams = {}) => {
  return http<SurveyAnswersResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-surveys/answers`,
    params,
  }).then(result => result.data)
}

export const downloadSurveyCsv = (params: SurveyCsvDownloadParams): Promise<AxiosResponse<Blob>> => {
  return http<Blob>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-surveys/download`,
    params,
    responseType: 'blob',
    staffMutationTreatAsWrite: true,
  })
}

export const fetchReceiptProviso = () => {
  return http<{ receiptProviso: ReceiptProvisoItem[] }>({
    method: 'get',
    url: `${env('BASE_API_V3')}/admin-receipt-proviso`,
  }).then(result => result.data)
}

export const updateReceiptProviso = (data: { receiptProvisos: ProvisoInput[] }) => {
  return http({
    method: 'put',
    url: `${env('BASE_API_V3')}/admin-receipt-proviso`,
    data,
  }).then(result => result.data)
}

export const registerPushSubscription = (data: PushSubscriptionRequest) => {
  return http({
    method: 'post',
    url: `${env('BASE_API_V3')}/push-subscriptions`,
    data,
  })
}

export const unregisterPushSubscription = (fingerprint: string) => {
  return http({
    method: 'delete',
    url: `${env('BASE_API_V3')}/push-subscriptions`,
    params: { fingerprint },
  })
}

export const fetchPushSubscriptionCtiLineSettings = (params: FetchPushSubscriptionCtiLineSettingsParams) => {
  return http<FetchPushSubscriptionCtiLineSettingsResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/push-subscriptions/cti-line-settings`,
    params,
  }).then(result => result.data)
}

export const updatePushSubscriptionCtiLineSettings = (params: UpdatePushSubscriptionCtiLineSettingsParams) => {
  return http<void>({
    method: 'put',
    url: `${env('BASE_API_V3')}/push-subscriptions/cti-line-settings`,
    data: params,
  })
}

export const fetchPriceRanks = () => {
  return http<PriceRank[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/price-ranks`,
  }).then(result => result.data)
}

export const createPriceRank = (data: PriceRankInput) => {
  return http<void>({
    method: 'post',
    url: `${env('BASE_API_V3')}/price-ranks`,
    data,
  }).then(result => result.data)
}

export const updatePriceRank = (id: string, data: PriceRankInput) => {
  return http<void>({
    method: 'put',
    url: `${env('BASE_API_V3')}/price-ranks/${id}`,
    data,
  }).then(result => result.data)
}

export const deletePriceRanks = (ids: string[]) => {
  // 他の一括削除同様 params で送る（DELETE body は届かない／未送信になることがある）
  return http<void>({
    method: 'delete',
    url: `${env('BASE_API_V3')}/price-ranks`,
    params: { ids },
  }).then(result => result.data)
}

export const fetchRateCalendars = (year: number, month: number) => {
  return http<RateCalendarEntry[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/rate-calendars`,
    params: { year, month },
  }).then(result => result.data)
}

export const updateRateCalendar = (data: RateCalendarUpdateInput) => {
  return http<void>({
    method: 'put',
    url: `${env('BASE_API_V3')}/rate-calendars`,
    data,
  }).then(result => result.data)
}

export const bulkUpdateRateCalendars = (data: RateCalendarBulkInput) => {
  return http<void>({
    method: 'put',
    url: `${env('BASE_API_V3')}/rate-calendars/bulk`,
    data,
  }).then(result => result.data)
}

export const fetchBaseRates = (params: BaseRateFetchParams) => {
  return http<BaseRateRow[]>({
    method: 'get',
    url: `${env('BASE_API_V3')}/base-rates`,
    params: params.sheetChargeType === 'rc' ? { sheetChargeType: 'rc' } : { sheetChargeType: 'per_person', paxBand: params.paxBand },
  }).then(result => result.data)
}

export const updateBaseRates = (data: BaseRateUpdateInput) => {
  return http<void>({
    method: 'put',
    url: `${env('BASE_API_V3')}/base-rates`,
    data,
  }).then(result => result.data)
}

export const fetchRoomRateDisplay = (params: RoomRateDisplayFetchParams) => {
  return http<RoomRateDisplayResponse>({
    method: 'get',
    url: `${env('BASE_API_V3')}/room-rate-display`,
    params:
      params.chargeType === 'rc'
        ? { from: params.from, chargeType: 'rc' }
        : { from: params.from, chargeType: 'per_person', pax: params.pax },
  }).then(result => result.data)
}
